// src/snowflake.ts
var SNOWFLAKE_EPOCH_MS = 17672256e5;
var EPOCH = BigInt(SNOWFLAKE_EPOCH_MS);
var TIMESTAMP_BITS = 42n;
var WORKER_BITS = 5n;
var PROCESS_BITS = 5n;
var SEQUENCE_BITS = 12n;
var PROCESS_SHIFT = SEQUENCE_BITS;
var WORKER_SHIFT = SEQUENCE_BITS + PROCESS_BITS;
var TIMESTAMP_SHIFT = SEQUENCE_BITS + PROCESS_BITS + WORKER_BITS;
var SNOWFLAKE_MAX_WORKER = Number((1n << WORKER_BITS) - 1n);
var SNOWFLAKE_MAX_PROCESS = Number((1n << PROCESS_BITS) - 1n);
var SNOWFLAKE_MAX_SEQUENCE = Number((1n << SEQUENCE_BITS) - 1n);
var MAX_TIMESTAMP = (1n << TIMESTAMP_BITS) - 1n;
var SNOWFLAKE_RE = /^(0|[1-9][0-9]{0,19})$/;
var MAX_ID = (1n << 64n) - 1n;
function checkRange(name, value, max) {
  if (!Number.isInteger(value) || value < 0 || value > max) {
    throw new RangeError(`${name} must be an integer from 0 to ${max}, got ${value}`);
  }
}
function makeSnowflake(parts) {
  checkRange("workerId", parts.workerId, SNOWFLAKE_MAX_WORKER);
  checkRange("processId", parts.processId, SNOWFLAKE_MAX_PROCESS);
  checkRange("sequence", parts.sequence, SNOWFLAKE_MAX_SEQUENCE);
  const delta = BigInt(Math.floor(parts.timestamp)) - EPOCH;
  if (delta < 0n || delta > MAX_TIMESTAMP) {
    throw new RangeError(`timestamp ${parts.timestamp} is outside the snowflake range`);
  }
  const id = delta << TIMESTAMP_SHIFT | BigInt(parts.workerId) << WORKER_SHIFT | BigInt(parts.processId) << PROCESS_SHIFT | BigInt(parts.sequence);
  return id.toString();
}
var SnowflakeGenerator = class {
  workerId;
  processId;
  now;
  lastTimestamp = -1;
  sequence = 0;
  constructor(options = {}) {
    this.workerId = options.workerId ?? 0;
    this.processId = options.processId ?? 0;
    checkRange("workerId", this.workerId, SNOWFLAKE_MAX_WORKER);
    checkRange("processId", this.processId, SNOWFLAKE_MAX_PROCESS);
    this.now = options.now ?? Date.now;
  }
  next() {
    let ts = Math.max(Math.floor(this.now()), this.lastTimestamp);
    if (ts === this.lastTimestamp) {
      this.sequence += 1;
      if (this.sequence > SNOWFLAKE_MAX_SEQUENCE) {
        ts += 1;
        this.sequence = 0;
      }
    } else {
      this.sequence = 0;
    }
    this.lastTimestamp = ts;
    return makeSnowflake({
      timestamp: ts,
      workerId: this.workerId,
      processId: this.processId,
      sequence: this.sequence
    });
  }
};
function isSnowflake(value) {
  if (typeof value !== "string" || !SNOWFLAKE_RE.test(value)) return false;
  return BigInt(value) <= MAX_ID;
}
function deconstructSnowflake(id) {
  if (!isSnowflake(id)) throw new TypeError(`not a snowflake: ${String(id)}`);
  const n = BigInt(id);
  return {
    timestamp: Number((n >> TIMESTAMP_SHIFT) + EPOCH),
    workerId: Number(n >> WORKER_SHIFT & BigInt(SNOWFLAKE_MAX_WORKER)),
    processId: Number(n >> PROCESS_SHIFT & BigInt(SNOWFLAKE_MAX_PROCESS)),
    sequence: Number(n & BigInt(SNOWFLAKE_MAX_SEQUENCE))
  };
}
function snowflakeTimestamp(id) {
  return deconstructSnowflake(id).timestamp;
}
function snowflakeDate(id) {
  return new Date(snowflakeTimestamp(id));
}
function snowflakeFromTimestamp(timestampMs) {
  return makeSnowflake({ timestamp: timestampMs, workerId: 0, processId: 0, sequence: 0 });
}
function compareSnowflakes(a, b) {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

// src/permissions.ts
var Permissions = {
  CREATE_INSTANT_INVITE: 1n << 0n,
  KICK_MEMBERS: 1n << 1n,
  BAN_MEMBERS: 1n << 2n,
  ADMINISTRATOR: 1n << 3n,
  MANAGE_CHANNELS: 1n << 4n,
  MANAGE_GUILD: 1n << 5n,
  ADD_REACTIONS: 1n << 6n,
  VIEW_AUDIT_LOG: 1n << 7n,
  PRIORITY_SPEAKER: 1n << 8n,
  STREAM: 1n << 9n,
  VIEW_CHANNEL: 1n << 10n,
  SEND_MESSAGES: 1n << 11n,
  SEND_TTS_MESSAGES: 1n << 12n,
  MANAGE_MESSAGES: 1n << 13n,
  EMBED_LINKS: 1n << 14n,
  ATTACH_FILES: 1n << 15n,
  READ_MESSAGE_HISTORY: 1n << 16n,
  MENTION_EVERYONE: 1n << 17n,
  USE_EXTERNAL_EMOJIS: 1n << 18n,
  VIEW_GUILD_INSIGHTS: 1n << 19n,
  CONNECT: 1n << 20n,
  SPEAK: 1n << 21n,
  MUTE_MEMBERS: 1n << 22n,
  DEAFEN_MEMBERS: 1n << 23n,
  MOVE_MEMBERS: 1n << 24n,
  USE_VAD: 1n << 25n,
  CHANGE_NICKNAME: 1n << 26n,
  MANAGE_NICKNAMES: 1n << 27n,
  /** In a channel's overwrites this is "Manage Permissions". */
  MANAGE_ROLES: 1n << 28n,
  MANAGE_WEBHOOKS: 1n << 29n,
  MANAGE_GUILD_EXPRESSIONS: 1n << 30n,
  USE_APPLICATION_COMMANDS: 1n << 31n,
  REQUEST_TO_SPEAK: 1n << 32n,
  MANAGE_EVENTS: 1n << 33n,
  MANAGE_THREADS: 1n << 34n,
  CREATE_PUBLIC_THREADS: 1n << 35n,
  CREATE_PRIVATE_THREADS: 1n << 36n,
  USE_EXTERNAL_STICKERS: 1n << 37n,
  SEND_MESSAGES_IN_THREADS: 1n << 38n,
  USE_EMBEDDED_ACTIVITIES: 1n << 39n,
  /** "Timeout Members". */
  MODERATE_MEMBERS: 1n << 40n,
  VIEW_CREATOR_MONETIZATION_ANALYTICS: 1n << 41n,
  USE_SOUNDBOARD: 1n << 42n,
  CREATE_GUILD_EXPRESSIONS: 1n << 43n,
  CREATE_EVENTS: 1n << 44n,
  USE_EXTERNAL_SOUNDS: 1n << 45n,
  SEND_VOICE_MESSAGES: 1n << 46n,
  // bit 47 is unused by Discord; we never use it either.
  SET_VOICE_CHANNEL_STATUS: 1n << 48n,
  SEND_POLLS: 1n << 49n,
  USE_EXTERNAL_APPS: 1n << 50n,
  PIN_MESSAGES: 1n << 51n,
  BYPASS_SLOWMODE: 1n << 52n
};
var NxPermissions = {
  /** Pick a voice channel's region (voice-regions §2.4). Implied by MANAGE_CHANNELS. */
  SET_VOICE_REGION: 1n << 0n,
  // bit 1: retired (the clan leader's "Manage channel access"); see RESERVED_NX_PERMISSIONS.
  /**
   * Comms only: defined and still computed, but hidden from every editor since
   * the Comms mode was removed (owner decision 1, 2026-10-28). Whisper to
   * people you can see; whispering to whole channels or to every commander
   * also needs CHANNEL_COMMANDER (comms-voice, 2026-10-27).
   */
  USE_WHISPER: 1n << 2n,
  /** Comms only, hidden like USE_WHISPER: TeamSpeak's channel commander. */
  CHANNEL_COMMANDER: 1n << 3n,
  /** Send a poke. */
  POKE: 1n << 4n,
  /** Join a password-protected channel without typing it. Implied by MANAGE_CHANNELS. */
  BYPASS_CHANNEL_PASSWORD: 1n << 5n
};
function orAll(values) {
  let out = 0n;
  for (const v of values) out |= v;
  return out;
}
var ALL_PERMISSIONS = orAll(Object.values(Permissions));
var ALL_NX_PERMISSIONS = orAll(Object.values(NxPermissions));
var RESERVED_NX_PERMISSIONS = 1n << 1n;
var SERVER_ONLY_PERMISSIONS = Permissions.KICK_MEMBERS | Permissions.BAN_MEMBERS | Permissions.ADMINISTRATOR | Permissions.MANAGE_GUILD | Permissions.VIEW_AUDIT_LOG | Permissions.VIEW_GUILD_INSIGHTS | Permissions.CHANGE_NICKNAME | Permissions.MANAGE_NICKNAMES | Permissions.MANAGE_GUILD_EXPRESSIONS | Permissions.MODERATE_MEMBERS | Permissions.VIEW_CREATOR_MONETIZATION_ANALYTICS | Permissions.CREATE_GUILD_EXPRESSIONS;
var COMMS_ONLY_NX_PERMISSIONS = NxPermissions.USE_WHISPER | NxPermissions.CHANNEL_COMMANDER;
var SEND_MESSAGES_DEPENDENT = Permissions.MENTION_EVERYONE | Permissions.SEND_TTS_MESSAGES | Permissions.ATTACH_FILES | Permissions.EMBED_LINKS;
var CONNECT_DEPENDENT = Permissions.MANAGE_CHANNELS | Permissions.PRIORITY_SPEAKER | Permissions.STREAM | Permissions.SPEAK | Permissions.MUTE_MEMBERS | Permissions.DEAFEN_MEMBERS | Permissions.MOVE_MEMBERS | Permissions.USE_VAD | Permissions.REQUEST_TO_SPEAK | Permissions.USE_EMBEDDED_ACTIVITIES | Permissions.USE_SOUNDBOARD | Permissions.USE_EXTERNAL_SOUNDS | Permissions.SET_VOICE_CHANNEL_STATUS;
var VOICE_NX = NxPermissions.SET_VOICE_REGION | NxPermissions.USE_WHISPER | NxPermissions.CHANNEL_COMMANDER | NxPermissions.BYPASS_CHANNEL_PASSWORD;
var TIMED_OUT_ALLOWED = Permissions.VIEW_CHANNEL | Permissions.READ_MESSAGE_HISTORY;
var DEFAULT_EVERYONE_PERMISSIONS = Permissions.ADD_REACTIONS | Permissions.STREAM | Permissions.VIEW_CHANNEL | Permissions.SEND_MESSAGES | Permissions.EMBED_LINKS | Permissions.ATTACH_FILES | Permissions.READ_MESSAGE_HISTORY | Permissions.USE_EXTERNAL_EMOJIS | Permissions.CONNECT | Permissions.SPEAK | Permissions.USE_VAD | Permissions.CHANGE_NICKNAME | Permissions.USE_APPLICATION_COMMANDS | Permissions.REQUEST_TO_SPEAK | Permissions.CREATE_PUBLIC_THREADS | Permissions.CREATE_PRIVATE_THREADS | Permissions.USE_EXTERNAL_STICKERS | Permissions.SEND_MESSAGES_IN_THREADS | Permissions.USE_EMBEDDED_ACTIVITIES | Permissions.USE_SOUNDBOARD | Permissions.USE_EXTERNAL_SOUNDS | Permissions.SEND_VOICE_MESSAGES | Permissions.SEND_POLLS | Permissions.USE_EXTERNAL_APPS | // Discord's default (2026-11-01): anyone in a voice channel sets its status
Permissions.SET_VOICE_CHANNEL_STATUS;
var DEFAULT_EVERYONE_NX_PERMISSIONS = NxPermissions.USE_WHISPER | NxPermissions.POKE;
var DECIMAL_RE = /^(0|[1-9][0-9]*)$/;
function parsePermissions(value) {
  if (value === null || value === void 0) return 0n;
  let n;
  if (typeof value === "bigint") n = value;
  else if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) throw new TypeError(`unsafe permission number: ${value}`);
    n = BigInt(value);
  } else {
    if (!DECIMAL_RE.test(value))
      throw new TypeError(`bad permission string: ${JSON.stringify(value)}`);
    n = BigInt(value);
  }
  if (n < 0n || n >= 1n << 63n) throw new RangeError(`permission value out of range: ${n}`);
  return n;
}
function serializePermissions(value) {
  return value.toString();
}
function hasPermission(permissions, flags) {
  return (permissions & flags) === flags;
}
function hasAnyPermission(permissions, flags) {
  return (permissions & flags) !== 0n;
}
function permissionNames(permissions) {
  return Object.keys(Permissions).filter(
    (k) => (permissions & Permissions[k]) !== 0n
  );
}
function nxPermissionNames(nx) {
  return Object.keys(NxPermissions).filter(
    (k) => (nx & NxPermissions[k]) !== 0n
  );
}
function permissionsFrom(...names) {
  return orAll(names.map((n) => Permissions[n]));
}
function nxPermissionsFrom(...names) {
  return orAll(names.map((n) => NxPermissions[n]));
}
var OverwriteType = {
  ROLE: 0,
  MEMBER: 1
};
var VOICE_CHANNEL_TYPES = /* @__PURE__ */ new Set([2, 13]);
function isVoiceChannelType(type) {
  return VOICE_CHANNEL_TYPES.has(type);
}
function isTimedOut(until, now = Date.now()) {
  if (until === null || until === void 0) return false;
  const t = until instanceof Date ? until.getTime() : typeof until === "number" ? until : Date.parse(until);
  return Number.isFinite(t) && t > now;
}
var ALL = Object.freeze({
  permissions: ALL_PERMISSIONS,
  nx_permissions: ALL_NX_PERMISSIONS
});
var NONE = Object.freeze({
  permissions: 0n,
  nx_permissions: 0n
});
function rawBase(ctx) {
  if (ctx.member.user_id === ctx.guild.owner_id) {
    return { privileged: true, permissions: ALL_PERMISSIONS, nx: ALL_NX_PERMISSIONS };
  }
  const byId = /* @__PURE__ */ new Map();
  for (const r of ctx.roles) byId.set(r.id, r);
  const everyone = byId.get(ctx.guild.id);
  let p = everyone?.permissions ?? 0n;
  let nx = everyone?.nx_permissions ?? 0n;
  for (const id of ctx.member.roles) {
    const role = byId.get(id);
    if (!role) continue;
    p |= role.permissions;
    nx |= role.nx_permissions ?? 0n;
  }
  if ((p & Permissions.ADMINISTRATOR) !== 0n) {
    return { privileged: true, permissions: ALL_PERMISSIONS, nx: ALL_NX_PERMISSIONS };
  }
  return { privileged: false, permissions: p, nx };
}
function impliedNx(p) {
  let nx = 0n;
  if ((p & Permissions.MANAGE_CHANNELS) !== 0n) {
    nx |= NxPermissions.SET_VOICE_REGION | NxPermissions.BYPASS_CHANNEL_PASSWORD;
  }
  return nx;
}
function computeBasePermissions(ctx) {
  const base = rawBase(ctx);
  if (base.privileged) return { ...ALL };
  if (isTimedOut(ctx.member.communication_disabled_until, ctx.now ?? Date.now())) {
    return { permissions: base.permissions & TIMED_OUT_ALLOWED, nx_permissions: 0n };
  }
  return {
    permissions: base.permissions,
    nx_permissions: (base.nx | impliedNx(base.permissions)) & ~RESERVED_NX_PERMISSIONS
  };
}
function computeChannelPermissions(ctx, channel) {
  const base = rawBase(ctx);
  if (base.privileged) return { ...ALL };
  let { p, nx } = overwritten(ctx, channel, base);
  if ((p & Permissions.VIEW_CHANNEL) === 0n) return { ...NONE };
  if ((p & Permissions.SEND_MESSAGES) === 0n) p &= ~SEND_MESSAGES_DEPENDENT;
  if (isVoiceChannelType(channel.type) && (p & Permissions.CONNECT) === 0n) {
    p &= ~CONNECT_DEPENDENT;
    nx &= ~VOICE_NX;
  }
  if (isTimedOut(ctx.member.communication_disabled_until, ctx.now ?? Date.now())) {
    return { permissions: p & TIMED_OUT_ALLOWED, nx_permissions: 0n };
  }
  return { permissions: p, nx_permissions: nx };
}
var MOVED_IN_VOICE_PERMISSIONS = Permissions.SPEAK | Permissions.USE_VAD | Permissions.PRIORITY_SPEAKER | Permissions.STREAM;
function computeMovedInVoicePermissions(ctx, channel) {
  if (!isVoiceChannelType(channel.type)) return 0n;
  const base = rawBase(ctx);
  if (base.privileged) return MOVED_IN_VOICE_PERMISSIONS;
  if (isTimedOut(ctx.member.communication_disabled_until, ctx.now ?? Date.now())) return 0n;
  return overwritten(ctx, channel, base).p & MOVED_IN_VOICE_PERMISSIONS;
}
function overwritten(ctx, channel, base) {
  let p = base.permissions;
  let nx = base.nx;
  const overwrites = channel.permission_overwrites;
  const memberRoles = new Set(ctx.member.roles);
  const everyone = overwrites.find((o) => o.type === OverwriteType.ROLE && o.id === ctx.guild.id);
  if (everyone) {
    p = p & ~everyone.deny | everyone.allow;
    nx = nx & ~(everyone.nx_deny ?? 0n) | (everyone.nx_allow ?? 0n);
  }
  let allow = 0n;
  let deny = 0n;
  let nxAllow = 0n;
  let nxDeny = 0n;
  for (const o of overwrites) {
    if (o.type !== OverwriteType.ROLE || o.id === ctx.guild.id || !memberRoles.has(o.id)) continue;
    allow |= o.allow;
    deny |= o.deny;
    nxAllow |= o.nx_allow ?? 0n;
    nxDeny |= o.nx_deny ?? 0n;
  }
  p = p & ~deny | allow;
  nx = nx & ~nxDeny | nxAllow;
  const own = overwrites.find(
    (o) => o.type === OverwriteType.MEMBER && o.id === ctx.member.user_id
  );
  if (own) {
    p = p & ~own.deny | own.allow;
    nx = nx & ~(own.nx_deny ?? 0n) | (own.nx_allow ?? 0n);
  }
  nx = (nx | impliedNx(p)) & ~RESERVED_NX_PERMISSIONS;
  return { p, nx };
}
var PRIVATE_THREAD_TYPE = 12;
var ARCHIVED_THREAD_DROPS = Permissions.ADD_REACTIONS | Permissions.PIN_MESSAGES;
function computeThreadPermissions(ctx, parent, thread) {
  const base = rawBase(ctx);
  if (base.privileged)
    return {
      permissions: thread.archived ? ALL_PERMISSIONS & ~ARCHIVED_THREAD_DROPS : ALL_PERMISSIONS,
      nx_permissions: ALL_NX_PERMISSIONS
    };
  const o = overwritten(ctx, parent, base);
  let p = o.p;
  const nx = o.nx;
  if ((p & Permissions.VIEW_CHANNEL) === 0n) return { ...NONE };
  const manages = (p & Permissions.MANAGE_THREADS) !== 0n;
  if (thread.type === PRIVATE_THREAD_TYPE && !thread.member && !manages) return { ...NONE };
  const canSend = (p & Permissions.SEND_MESSAGES_IN_THREADS) !== 0n && (!thread.locked || manages);
  p = canSend ? p | Permissions.SEND_MESSAGES : p & ~Permissions.SEND_MESSAGES;
  if (!canSend) p &= ~(SEND_MESSAGES_DEPENDENT | Permissions.SEND_MESSAGES_IN_THREADS);
  if (thread.archived) p &= ~ARCHIVED_THREAD_DROPS;
  if (isTimedOut(ctx.member.communication_disabled_until, ctx.now ?? Date.now())) {
    return { permissions: p & TIMED_OUT_ALLOWED, nx_permissions: 0n };
  }
  return { permissions: p, nx_permissions: nx & ~VOICE_NX };
}
var PRIVATE_CHANNEL_PERMISSIONS = Permissions.VIEW_CHANNEL | Permissions.SEND_MESSAGES | Permissions.READ_MESSAGE_HISTORY | Permissions.ADD_REACTIONS | Permissions.ATTACH_FILES | Permissions.EMBED_LINKS | Permissions.PIN_MESSAGES | Permissions.SEND_POLLS;

// src/roles.ts
function compareRolesTopFirst(a, b) {
  return b.position - a.position || compareSnowflakes(a.id, b.id);
}
function roleRank(roles, role, everyoneId) {
  if (role.id === everyoneId) return 0;
  let newerSame = 0;
  for (const o of roles) {
    if (o.position === role.position && o.id !== role.id && compareSnowflakes(o.id, role.id) > 0)
      newerSame += 1;
  }
  return role.position + newerSame / 1e3;
}
function memberTopRank(guild, roles, userId, memberRoleIds) {
  if (userId === guild.owner_id) return Number.POSITIVE_INFINITY;
  let best = 0;
  for (const id of memberRoleIds) {
    const r = roles.find((x) => x.id === id);
    if (r) best = Math.max(best, roleRank(roles, r, guild.id));
  }
  return best;
}
var ROLE_POKE_RULES = ["everyone", "higher", "nobody"];
function pokeAllowedByRoles(roles, everyoneId, senderTopRank, targetRoleIds) {
  if (senderTopRank === Number.POSITIVE_INFINITY) return true;
  let top;
  let topRank = -1;
  for (const r of roles) {
    if (r.id !== everyoneId && !targetRoleIds.includes(r.id)) continue;
    const rank = roleRank(roles, r, everyoneId);
    if (rank > topRank) [top, topRank] = [r, rank];
  }
  const rule = top?.nx_pokes_from ?? "everyone";
  if (rule === "nobody") return false;
  if (rule === "higher") return senderTopRank > topRank;
  return true;
}
var ROLE_ICON_MAX_BYTES = 256 * 1024;
var ROLE_ICON_SIZE = 64;
var ROLE_ICON_MAX_SIDE = 1024;
function iconRoleOf(roles, memberRoleIds) {
  let best = null;
  for (const r of roles) {
    if (!memberRoleIds.includes(r.id) || !r.icon && !r.unicode_emoji) continue;
    if (!best || compareRolesTopFirst(r, best) < 0) best = r;
  }
  return best;
}
var ROLE_ICON_MODES = ["highest", "all"];
var ROLE_ICONS_SHOWN_MAX = 3;
function iconRolesOf(roles, memberRoleIds) {
  return roles.filter((r) => memberRoleIds.includes(r.id) && (!!r.icon || !!r.unicode_emoji)).sort(compareRolesTopFirst);
}
function iconRolesForMode(roles, memberRoleIds, mode) {
  if (mode === "all") return iconRolesOf(roles, memberRoleIds);
  const top = iconRoleOf(roles, memberRoleIds);
  return top ? [top] : [];
}
var PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
var FLAG = /^\p{Regional_Indicator}{2}$/u;
var KEYCAP = /^[#*0-9]️?⃣$/u;
function isSingleEmoji(value) {
  if (!value || value.length > 32 || value.trim() !== value) return false;
  const seg = new Intl.Segmenter(void 0, { granularity: "grapheme" });
  const parts = [...seg.segment(value)];
  if (parts.length !== 1) return false;
  return PICTOGRAPHIC.test(value) || FLAG.test(value) || KEYCAP.test(value);
}
function permissionContextFromWire(guild, userId, member) {
  return {
    guild: { id: guild.id, owner_id: guild.owner_id },
    roles: guild.roles.map((r) => ({
      id: r.id,
      permissions: parsePermissions(r.permissions),
      nx_permissions: parsePermissions(r.nx_permissions ?? "0")
    })),
    member: {
      user_id: userId,
      roles: member?.roles ?? [],
      communication_disabled_until: member?.communication_disabled_until ?? null
    }
  };
}
function permissionChannelFromWire(channel) {
  return {
    id: channel.id,
    type: channel.type,
    permission_overwrites: (channel.permission_overwrites ?? []).map((o) => ({
      id: o.id,
      type: o.type === 1 ? 1 : 0,
      allow: parsePermissions(o.allow),
      deny: parsePermissions(o.deny),
      nx_allow: parsePermissions(o.nx_allow ?? "0"),
      nx_deny: parsePermissions(o.nx_deny ?? "0")
    }))
  };
}
function overwritesInSync(channel, category) {
  const a = channel ?? [];
  const b = category ?? [];
  if (a.length !== b.length) return false;
  const key = (o) => [o.type === 1 ? 1 : 0, o.allow, o.deny, o.nx_allow ?? "0", o.nx_deny ?? "0"].join(":");
  const byId = new Map(b.map((o) => [o.id, key(o)]));
  return a.every((o) => byId.get(o.id) === key(o));
}

// src/models.ts
var UserFlags = {
  STAFF: 1 << 0,
  /**
   * Ours (Console v2, 2026-11-02): staff verified this username (a real
   * person or brand). Shown as a badge by `VerifiedBadge`.
   */
  NX_VERIFIED: 1 << 27,
  /** Ours: an official account badge (Discord has no public equivalent). */
  NX_OFFICIAL: 1 << 28,
  /** Ours: the `system` account that sends safety messages. */
  NX_SYSTEM: 1 << 29
};
var ActivityType = {
  PLAYING: 0,
  STREAMING: 1,
  LISTENING: 2,
  WATCHING: 3,
  CUSTOM: 4,
  COMPETING: 5
};
var BACKGROUND_SETTINGS = [
  "glow",
  "black",
  "charcoal",
  "midnight",
  "white",
  "grey"
];
var DEFAULT_PRIVACY_SETTINGS = {
  dms_from_members: true,
  message_requests: true,
  friend_requests_from: { everyone: true, friends_of_friends: true, server_members: true },
  pokes_from: "everyone"
};
var ChannelType = {
  GUILD_TEXT: 0,
  DM: 1,
  GUILD_VOICE: 2,
  GROUP_DM: 3,
  GUILD_CATEGORY: 4,
  GUILD_ANNOUNCEMENT: 5,
  ANNOUNCEMENT_THREAD: 10,
  PUBLIC_THREAD: 11,
  PRIVATE_THREAD: 12,
  GUILD_STAGE_VOICE: 13,
  GUILD_DIRECTORY: 14,
  GUILD_FORUM: 15,
  GUILD_MEDIA: 16
};
var MessageType = {
  DEFAULT: 0,
  RECIPIENT_ADD: 1,
  RECIPIENT_REMOVE: 2,
  CALL: 3,
  CHANNEL_NAME_CHANGE: 4,
  CHANNEL_ICON_CHANGE: 5,
  CHANNEL_PINNED_MESSAGE: 6,
  USER_JOIN: 7,
  /** "{author} started a thread: {name}" in the parent channel (a thread made without a message). */
  THREAD_CREATED: 18,
  REPLY: 19,
  /** A thread's first message, pointing at the parent channel's message it was started from. */
  THREAD_STARTER_MESSAGE: 21,
  /** A poll ended (polls.ts): by the poll's author, pointing at the poll (`message_reference`). */
  POLL_RESULT: 46,
  /**
   * An AutoMod alert in a rule's alert channel (added 2026-10-29, AutoMod):
   * the flagged person is the author, their message the content, and
   * `embeds[0]` an `AutoModAlertEmbed` (automod.ts). Nobody edits it; only
   * Manage Messages holders delete it.
   */
  AUTO_MODERATION_ACTION: 24
};
var MessageFlags = {
  /** A thread was started from this message (its ID is the message's). */
  HAS_THREAD: 1 << 5,
  SUPPRESS_EMBEDS: 1 << 2,
  SUPPRESS_NOTIFICATIONS: 1 << 12,
  /** A forward: `message_snapshots` holds the forwarded message (polls.ts). Set by the server. */
  HAS_SNAPSHOT: 1 << 14
};
var RelationshipType = {
  NONE: 0,
  FRIEND: 1,
  BLOCKED: 2,
  PENDING_INCOMING: 3,
  PENDING_OUTGOING: 4
};
var AuditLogEvent = {
  GUILD_UPDATE: 1,
  CHANNEL_CREATE: 10,
  CHANNEL_UPDATE: 11,
  CHANNEL_DELETE: 12,
  CHANNEL_OVERWRITE_CREATE: 13,
  CHANNEL_OVERWRITE_UPDATE: 14,
  CHANNEL_OVERWRITE_DELETE: 15,
  MEMBER_KICK: 20,
  MEMBER_PRUNE: 21,
  MEMBER_BAN_ADD: 22,
  MEMBER_BAN_REMOVE: 23,
  MEMBER_UPDATE: 24,
  MEMBER_ROLE_UPDATE: 25,
  MEMBER_MOVE: 26,
  MEMBER_DISCONNECT: 27,
  ROLE_CREATE: 30,
  ROLE_UPDATE: 31,
  ROLE_DELETE: 32,
  INVITE_CREATE: 40,
  INVITE_UPDATE: 41,
  INVITE_DELETE: 42,
  /** Custom emoji (added 2026-11-01, Discord's codes). */
  EMOJI_CREATE: 60,
  EMOJI_UPDATE: 61,
  EMOJI_DELETE: 62,
  MESSAGE_DELETE: 72,
  MESSAGE_BULK_DELETE: 73,
  MESSAGE_PIN: 74,
  MESSAGE_UNPIN: 75,
  // scheduled events (2026-10-29): Discord's codes
  GUILD_SCHEDULED_EVENT_CREATE: 100,
  GUILD_SCHEDULED_EVENT_UPDATE: 101,
  GUILD_SCHEDULED_EVENT_DELETE: 102,
  /** Threads (2026-10-29): target is the thread; `options.channel_id` its parent. */
  THREAD_CREATE: 110,
  THREAD_UPDATE: 111,
  THREAD_DELETE: 112,
  /**
   * AutoMod (added 2026-10-29), Discord's codes: rule changes (target: the
   * rule), and what a rule did (target: the member; no user, `nx_source`
   * `system`; options `auto_moderation_rule_name`,
   * `auto_moderation_rule_trigger_type`, `channel_id`).
   */
  AUTO_MODERATION_RULE_CREATE: 140,
  AUTO_MODERATION_RULE_UPDATE: 141,
  AUTO_MODERATION_RULE_DELETE: 142,
  AUTO_MODERATION_BLOCK_MESSAGE: 143,
  AUTO_MODERATION_FLAG_TO_CHANNEL: 144,
  AUTO_MODERATION_USER_COMMUNICATION_DISABLED: 145,
  /**
   * Renumbered 2026-10-27 (P1 SERVER track) to the user-management spec's
   * codes (§4.8: 10013 and 10014; 10001-10002 are bulk ban and profile hide).
   * The names are unchanged; migration 0002 rewrites stored rows.
   */
  /** Discord's voice channel status set / cleared (2026-11-01). */
  VOICE_CHANNEL_STATUS_UPDATE: 192,
  VOICE_CHANNEL_STATUS_DELETE: 193,
  NX_CHANNEL_BAN_ADD: 10013,
  NX_CHANNEL_BAN_REMOVE: 10014,
  /**
   * Not in the spec's table (which logs password changes as CHANNEL_UPDATE).
   * Kept for compatibility; collides with the spec's P2 code 10003
   * (NX_MEMBER_PROFILE_UNHIDE) and must move before P2 (contract-changes.md).
   */
  NX_CHANNEL_PASSWORD_UPDATE: 10003,
  /** A deleted channel brought back within 7 days (§4.5). */
  NX_CHANNEL_RESTORE: 10015,
  /** A deleted role brought back within 7 days (§4.5). */
  NX_ROLE_RESTORE: 10016,
  /**
   * Someone with Manage Messages ended another person's poll early (polls.ts,
   * 2026-10-29). `target_id` is the poll's author; options `channel_id`,
   * `message_id`.
   */
  NX_POLL_END: 10017
};
var NxDataFix = {
  /** No clans: the seeded Clans channels removed. */
  REMOVE_SEEDED_CLANS: "2026-10-28-remove-seeded-clans",
  /** The seeded voice channels renamed (Room 1, Room 2). */
  RENAME_SEEDED_SQUADS: "2026-10-28-rename-seeded-squads",
  /** A voice channel's talk-power need turned into an @everyone Speak deny. */
  RETIRE_TALK_POWER: "2026-10-28-talk-power-retired",
  /** English only (2026-10-29): what we seeded reads in English. */
  ENGLISH_ONLY: "2026-10-29-english-only",
  /** The official #announcements: @everyone can't start threads or write in them either. */
  ANNOUNCEMENT_THREADS: "2026-11-02-announcement-threads"
};

// src/errors.ts
var ApiErrorCode = {
  /** Generic error (Discord's 0). */
  GENERAL: 0,
  UNKNOWN_CHANNEL: 10003,
  UNKNOWN_GUILD: 10004,
  UNKNOWN_INVITE: 10006,
  UNKNOWN_MEMBER: 10007,
  UNKNOWN_MESSAGE: 10008,
  UNKNOWN_OVERWRITE: 10009,
  UNKNOWN_ROLE: 10011,
  UNKNOWN_USER: 10013,
  UNKNOWN_EMOJI: 10014,
  UNKNOWN_SESSION: 10020,
  UNKNOWN_BAN: 10026,
  /** Console v2 (2026-11-02, Discord's): the account is in `limits.guilds_per_user` servers (HTTP 400). */
  MAX_GUILDS: 30001,
  MAX_FRIENDS: 30002,
  MAX_PINS: 30003,
  MAX_RECIPIENTS: 30004,
  MAX_ROLES: 30005,
  /** Custom emoji (added 2026-11-01, Discord's codes): still, then animated. */
  MAX_EMOJIS: 30008,
  MAX_REACTIONS: 30010,
  MAX_CHANNELS: 30013,
  MAX_INVITES: 30016,
  MAX_ANIMATED_EMOJIS: 30018,
  UNAUTHORIZED: 40001,
  /**
   * Discord's: "You must transfer ownership of any owned guilds before
   * deleting your account" (also for Disable account). Added 2026-10-29.
   */
  MUST_TRANSFER_OWNERSHIP: 40011,
  REQUEST_TOO_LARGE: 40005,
  USER_BANNED: 40007,
  MISSING_ACCESS: 50001,
  CANNOT_EDIT_OTHERS_MESSAGE: 50005,
  EMPTY_MESSAGE: 50006,
  CANNOT_MESSAGE_USER: 50007,
  CANNOT_SEND_IN_NON_TEXT_CHANNEL: 50008,
  /** Added 2026-10-29 (AutoMod): an AutoMod alert can't be edited (Discord's code). */
  CANNOT_EDIT_SYSTEM_MESSAGE: 50021,
  MISSING_PERMISSIONS: 50013,
  INVALID_TOKEN: 50014,
  INVALID_CHANNEL_TYPE: 50024,
  INVALID_FORM_BODY: 50035,
  FRIEND_REQUEST_BLOCKED: 80001,
  // ---- threads (Discord's codes, added 2026-10-29) ----
  /** The thread is archived: unarchive it first (sending a message does, unless it is locked). */
  THREAD_ARCHIVED: 50083,
  /** The thread's member limit is reached. */
  MAX_THREAD_MEMBERS: 30033,
  /** That message already has a thread. */
  THREAD_ALREADY_CREATED: 160004,
  /** The thread is locked: only Manage Threads may write in it. */
  THREAD_LOCKED: 160005,
  /** The server's active thread limit is reached (`limits.active_threads`). */
  MAX_ACTIVE_THREADS: 160006,
  // ---- added 2026-10-29, polls (Discord's codes) ----
  /** The poll has ended: no more votes, and it can't be ended again. */
  POLL_EXPIRED: 520001,
  /** A poll message can't be edited. */
  CANNOT_EDIT_POLL: 520003,
  /** An answer's emoji can't be used. */
  POLL_EMOJI_INVALID: 520004,
  /** Only a poll can be ended. */
  CANNOT_EXPIRE_NON_POLL: 520006,
  /** HTTP 429. */
  RATE_LIMITED: 429,
  // ---- ours (100000+) ----
  STEP_UP_REQUIRED: 100001,
  MFA_REQUIRED_FOR_MODERATION: 100002,
  LIMITED_ACCESS: 100004,
  JOINING_PAUSED: 100005,
  /**
   * UNUSED since 2026-10-28: the new-account period was removed (owner
   * decision, docs/decisions/2026-10-28-email-accounts.md). The number stays
   * reserved and nothing sends it.
   */
  NEW_ACCOUNT_PERIOD: 100006,
  SECOND_FACTOR_REQUIRED: 100007,
  CONTACTS_LOCKED: 100010,
  /** "The email or password is wrong." Never says which. */
  INVALID_CREDENTIALS: 100011,
  INVALID_ALPHA_CODE: 100012,
  UNDERAGE: 100013,
  INVALID_MFA_CODE: 100014,
  INVALID_MFA_TICKET: 100015,
  INVALID_REFRESH_TOKEN: 100016,
  POW_REQUIRED: 100017,
  POW_INVALID: 100018,
  PASSWORD_LEAKED: 100019,
  CHANNEL_PASSWORD_REQUIRED: 100020,
  CHANNEL_PASSWORD_WRONG: 100021,
  CHANNEL_FULL: 100022,
  CHANNEL_BANNED: 100023,
  MEMBER_TIMED_OUT: 100024,
  ROLE_HIERARCHY: 100025,
  // ---- added 2026-10-27, P1 SERVER track ----
  /** One recovery runs at a time (§2.5). */
  RECOVERY_IN_PROGRESS: 100026,
  /** A cancelled recovery can't be retried for 24 hours. */
  RECOVERY_COOLDOWN: 100027,
  /** The 72-hour hold is not over, or the device has not approved yet. */
  RECOVERY_NOT_READY: 100028,
  /** "Couldn't poke": the same for a block, "Pokes from" or not reachable (§3.2). */
  POKE_FAILED: 100029,
  /** No longer sent (2026-10-29): ownership transfers no longer wait. */
  OWNER_TRANSFER_PENDING: 100030,
  /**
   * A destructive action needs a two-step code from the last 5 minutes (the
   * password where the server doesn't require two-step): string
   * `mod.brake.destructive`. Step up, then retry.
   */
  FRESH_STEP_UP_REQUIRED: 100031,
  /** Over 10 bans, kicks or timeouts in 10 minutes: string `mod.brake`. Step up, then retry. */
  MODERATOR_BRAKE: 100032,
  /** No longer sent (2026-10-29): the new owner no longer needs two-step. */
  NEW_OWNER_NEEDS_MFA: 100033,
  // ---- added 2026-10-28, Console v1 (admin-dashboard §4.9, §4.10) ----
  /** A feature flag is off for this user (HTTP 403, `nx_feature`). The server enforces every flag. */
  FEATURE_DISABLED: 100034,
  /** Maintenance mode (HTTP 503, `nx_maintenance_ends_at`). The console's staff sign-in still works. */
  MAINTENANCE: 100035,
  /** Sign-up is closed: the mode is `closed` or the `signups` flag is off (HTTP 403). */
  SIGNUP_CLOSED: 100036,
  // ---- added 2026-10-28, Console v1 server (admin-dashboard §4.2) ----
  /** Staff suspended the account (HTTP 403, `nx_until`): sign-in and every session are refused. */
  ACCOUNT_SUSPENDED: 100037,
  /** Staff disabled the account, or it is in the 15-day deletion hold (HTTP 403; `nx_until` for the hold). */
  ACCOUNT_DISABLED: 100038,
  /** Staff limited this feature for the account (HTTP 403, `nx_until`, `nx_feature` = `AdminLimitFeature`). */
  ACCOUNT_LIMITED: 100039,
  // ---- added 2026-10-28, email accounts (docs/decisions/2026-10-28-email-accounts.md) ----
  /** The 6-digit email code is wrong (it still works for a few more tries). */
  EMAIL_CODE_INVALID: 100040,
  /**
   * The email code or link expired, was already used, was replaced by a newer
   * one, or had too many wrong tries: send a new one.
   */
  EMAIL_CODE_EXPIRED: 100041,
  /** The account has no email yet (made before email sign-in): add one first. */
  EMAIL_REQUIRED: 100042,
  /** The password-reset link expired, was already used or was replaced by a newer one. */
  RESET_LINK_INVALID: 100043,
  /** The mail server did not take the email (HTTP 503). Nothing was lost: try again later. */
  EMAIL_SEND_FAILED: 100044,
  // ---- added 2026-10-29, poke rules (owner: "poke is an in-server feature") ----
  /** You are offline or invisible: go online to poke someone (HTTP 403). */
  POKE_NEEDS_ONLINE: 100045,
  /** You are not in a voice channel of the server: "Join a voice channel to poke someone." (HTTP 403) */
  POKE_NEEDS_VOICE: 100046,
  /** The target is offline or invisible: "They're offline." (HTTP 403) */
  POKE_TARGET_OFFLINE: 100047,
  /**
   * The target's highest role says who may poke them (`Role.nx_pokes_from`
   * `higher` or `nobody`), and you are not allowed (HTTP 403). Role settings
   * are public to every member, so this hides nothing; it is checked before
   * blocks and "Pokes from", which stay the hidden `POKE_FAILED`.
   */
  POKE_ROLE_RESTRICTED: 100048,
  // ---- added 2026-10-29, AutoMod (Discord's codes) ----
  // ---- added 2026-11-02, Console v2 (docs/design/console-v2.md) ----
  /** `community.server_creation` is `off`, or `staff` and the caller isn't staff (HTTP 403). */
  GUILD_CREATION_DISABLED: 100049,
  /** Raid mode: joining by invite is paused for everyone but staff (HTTP 403). */
  RAID_MODE_ACTIVE: 100050,
  /** That action already has an appeal (one per violation; HTTP 409). */
  APPEAL_EXISTS: 100051,
  /** The appeal window (180 days) is over, or the action no longer stands (HTTP 400). */
  APPEAL_CLOSED: 100052,
  /** An AutoMod rule blocked the message (HTTP 400; `nx_automod_message`: the rule's response). */
  AUTOMOD_MESSAGE_BLOCKED: 2e5,
  /** An AutoMod rule blocked a thread or forum post title (HTTP 400). */
  AUTOMOD_TITLE_BLOCKED: 200001
};
var FieldErrorCode = {
  REQUIRED: "BASE_TYPE_REQUIRED",
  BAD_TYPE: "BASE_TYPE_BAD_TYPE",
  TOO_SHORT: "BASE_TYPE_MIN_LENGTH",
  TOO_LONG: "BASE_TYPE_MAX_LENGTH",
  INVALID: "BASE_TYPE_INVALID",
  USERNAME_TAKEN: "USERNAME_ALREADY_TAKEN",
  USERNAME_INVALID_CHARS: "USERNAME_INVALID_CHARACTERS",
  USERNAME_CONSECUTIVE_DOTS: "USERNAME_CONSECUTIVE_DOTS",
  USERNAME_RESERVED: "USERNAME_RESERVED",
  DISPLAY_NAME_BLANK: "DISPLAY_NAME_BLANK",
  PASSWORD_TOO_SHORT: "PASSWORD_TOO_SHORT",
  PASSWORD_TOO_LONG: "PASSWORD_TOO_LONG",
  BIRTH_DATE_INVALID: "BIRTH_DATE_INVALID",
  UNDERAGE: "BIRTH_DATE_UNDERAGE",
  // ---- added 2026-10-28, email accounts (Discord's codes) ----
  EMAIL_INVALID: "EMAIL_TYPE_INVALID_EMAIL",
  EMAIL_TAKEN: "EMAIL_ALREADY_REGISTERED",
  /** Ours: the address is the one the account already has. */
  EMAIL_UNCHANGED: "EMAIL_UNCHANGED"
};
function isApiError(value) {
  if (typeof value !== "object" || value === null) return false;
  const v = value;
  return typeof v.code === "number" && typeof v.message === "string";
}

// src/rest.ts
var API_VERSION = 1;
var API_BASE = "/api/v1";
var AUDIT_LOG_REASON_HEADER = "X-Audit-Log-Reason";
var e = encodeURIComponent;
var Routes = {
  health: () => "/health",
  gateway: () => "/gateway",
  // auth (ours)
  authPowChallenge: () => "/auth/pow/challenge",
  authRegister: () => "/auth/register",
  authLogin: () => "/auth/login",
  authMfaTotp: () => "/auth/mfa/totp",
  authMfaRecovery: () => "/auth/mfa/recovery",
  authRefresh: () => "/auth/refresh",
  authLogout: () => "/auth/logout",
  authStepUp: () => "/auth/step-up",
  // email (added 2026-10-28, email accounts)
  authVerifyEmail: () => "/auth/verify-email",
  authForgotPassword: () => "/auth/forgot-password",
  authResetPassword: () => "/auth/reset-password",
  authUndoEmailChange: () => "/auth/undo-email-change",
  // current user
  me: () => "/users/@me",
  mePassword: () => "/users/@me/password",
  meEmail: () => "/users/@me/email",
  meEmailVerify: () => "/users/@me/email/verify",
  meEmailResend: () => "/users/@me/email/resend",
  meEmailPending: () => "/users/@me/email/pending",
  meSettings: () => "/users/@me/settings",
  meSessions: () => "/users/@me/sessions",
  meSession: (sessionId) => `/users/@me/sessions/${e(sessionId)}`,
  meSessionsLogoutOthers: () => "/users/@me/sessions/logout-others",
  meMfaTotpSetup: () => "/users/@me/mfa/totp/setup",
  meMfaTotp: () => "/users/@me/mfa/totp",
  meMfaCodes: () => "/users/@me/mfa/codes",
  meRelationships: () => "/users/@me/relationships",
  meRelationship: (userId) => `/users/@me/relationships/${userId}`,
  meChannels: () => "/users/@me/channels",
  meReadStates: () => "/users/@me/read-states",
  user: (userId) => `/users/${userId}`,
  userProfile: (userId) => `/users/${userId}/profile`,
  userPoke: (userId) => `/users/${userId}/poke`,
  // guilds the user makes, joins and leaves (Console v2 server creation, 2026-11-02)
  /** POST: create a server (`CreateGuildRequest`, console-v2/community.ts). */
  guilds: () => "/guilds",
  /** GET: the servers the user is in. */
  meGuilds: () => "/users/@me/guilds",
  /** DELETE: leave a server (its owner can't). */
  meGuild: (guildId) => `/users/@me/guilds/${guildId}`,
  // reports and appeals (Console v2 moderation; console-v2/moderation.ts)
  /** POST: report a message, a user or a server. */
  reports: () => "/reports",
  /** GET the user's appeals, POST a new one. */
  meAppeals: () => "/users/@me/appeals",
  /** POST: a suspended account's appeal, with the sign-in refusal's `nx_appeal_token`. */
  authAppeal: () => "/auth/appeal",
  /** GET: the desktop updater's dynamic endpoint (Tauri's format; 204 = no update). */
  desktopUpdate: () => "/desktop/update",
  // guild
  /** GET, PATCH; DELETE deletes the server (owner only, Console v2). */
  guild: (guildId) => `/guilds/${guildId}`,
  guildChannels: (guildId) => `/guilds/${guildId}/channels`,
  guildRoles: (guildId) => `/guilds/${guildId}/roles`,
  guildRole: (guildId, roleId) => `/guilds/${guildId}/roles/${roleId}`,
  guildMembers: (guildId) => `/guilds/${guildId}/members`,
  guildMembersSearch: (guildId) => `/guilds/${guildId}/members/search`,
  guildMember: (guildId, userId) => `/guilds/${guildId}/members/${userId}`,
  guildMemberRole: (guildId, userId, roleId) => `/guilds/${guildId}/members/${userId}/roles/${roleId}`,
  guildBans: (guildId) => `/guilds/${guildId}/bans`,
  guildBan: (guildId, userId) => `/guilds/${guildId}/bans/${userId}`,
  guildAuditLogs: (guildId) => `/guilds/${guildId}/audit-logs`,
  guildInvites: (guildId) => `/guilds/${guildId}/invites`,
  guildVoiceStates: (guildId) => `/guilds/${guildId}/voice-states`,
  guildMessagesSearch: (guildId) => `/guilds/${guildId}/messages/search`,
  // channels
  channel: (channelId) => `/channels/${channelId}`,
  channelPermission: (channelId, overwriteId) => `/channels/${channelId}/permissions/${overwriteId}`,
  channelBans: (channelId) => `/channels/${channelId}/bans`,
  channelBan: (channelId, userId) => `/channels/${channelId}/bans/${userId}`,
  /** Ours (2026-10-28): channel mutes and deafens in a voice channel. */
  channelVoiceMutes: (channelId) => `/channels/${channelId}/voice-mutes`,
  channelVoiceMute: (channelId, userId) => `/channels/${channelId}/voice-mutes/${userId}`,
  channelInvites: (channelId) => `/channels/${channelId}/invites`,
  channelTyping: (channelId) => `/channels/${channelId}/typing`,
  channelRecipient: (channelId, userId) => `/channels/${channelId}/recipients/${userId}`,
  channelMessages: (channelId) => `/channels/${channelId}/messages`,
  channelMessagesSearch: (channelId) => `/channels/${channelId}/messages/search`,
  channelMessage: (channelId, messageId) => `/channels/${channelId}/messages/${messageId}`,
  channelMessageAck: (channelId, messageId) => `/channels/${channelId}/messages/${messageId}/ack`,
  channelPins: (channelId) => `/channels/${channelId}/messages/pins`,
  channelPin: (channelId, messageId) => `/channels/${channelId}/messages/pins/${messageId}`,
  messageReactions: (channelId, messageId) => `/channels/${channelId}/messages/${messageId}/reactions`,
  messageReaction: (channelId, messageId, emoji) => `/channels/${channelId}/messages/${messageId}/reactions/${e(emoji)}`,
  messageOwnReaction: (channelId, messageId, emoji) => `/channels/${channelId}/messages/${messageId}/reactions/${e(emoji)}/@me`,
  messageUserReaction: (channelId, messageId, emoji, userId) => `/channels/${channelId}/messages/${messageId}/reactions/${e(emoji)}/${userId}`,
  // polls (2026-10-29, polls.ts)
  pollVote: (channelId, messageId) => `/channels/${channelId}/polls/${messageId}/answers/@me`,
  pollAnswerVoters: (channelId, messageId, answerId) => `/channels/${channelId}/polls/${messageId}/answers/${answerId}`,
  pollExpire: (channelId, messageId) => `/channels/${channelId}/polls/${messageId}/expire`,
  // invites
  invite: (code) => `/invites/${e(code)}`,
  // voice
  voiceToken: () => "/voice/token",
  // public client config (admin.ts `ClientConfig`; works signed out). Added 2026-10-28, Console v1.
  config: () => "/config"
};
function isMfaRequired(r) {
  return r.mfa === true;
}
var EMAIL_CODE_TTL_MS = 24 * 60 * 60 * 1e3;
var PASSWORD_RESET_TTL_MS = 60 * 60 * 1e3;
var EMAIL_CODE_MAX_ATTEMPTS = 5;
var EMAIL_LOCK_AFTER_RECOVERY_MS = 7 * 24 * 60 * 60 * 1e3;
var GROUP_DM_MAX_RECIPIENTS = 10;
var INTERNAL_ALPHA_CODES_PATH = "/internal/admin/alpha-codes";
var P1Routes = {
  meMessageRequests: () => "/users/@me/message-requests",
  channelRequest: (channelId) => `/channels/${channelId}/request`,
  meIgnores: () => "/users/@me/ignores",
  meIgnore: (userId) => `/users/@me/ignores/${userId}`,
  userPoke: (userId) => `/users/${userId}/poke`,
  mePokes: () => "/users/@me/pokes",
  recoveryStart: () => "/auth/recovery/start",
  recoveryComplete: () => "/auth/recovery/complete",
  recoveryApprove: () => "/auth/recovery/approve",
  recoveryCancel: () => "/auth/recovery/cancel",
  recoveryPending: () => "/auth/recovery/pending",
  guildOwnerTransfer: (guildId) => `/guilds/${guildId}/owner-transfer`,
  guildDeleted: (guildId) => `/guilds/${guildId}/deleted`,
  guildDeletedRestore: (guildId, itemId) => `/guilds/${guildId}/deleted/${itemId}/restore`,
  channelInvites: (channelId) => `/channels/${channelId}/invites`,
  guildInvites: (guildId) => `/guilds/${guildId}/invites`,
  invite: (code) => `/invites/${encodeURIComponent(code)}`
};

// src/gateway.ts
var GATEWAY_VERSION = 1;
var GATEWAY_PATH = "/gateway";
var DEFAULT_HEARTBEAT_INTERVAL_MS = 41250;
var GATEWAY_CLIENT_RATE_LIMIT = 120;
var GatewayOpcodes = {
  DISPATCH: 0,
  HEARTBEAT: 1,
  IDENTIFY: 2,
  PRESENCE_UPDATE: 3,
  RESUME: 6,
  RECONNECT: 7,
  INVALID_SESSION: 9,
  HELLO: 10,
  HEARTBEAT_ACK: 11
};
var GatewayCloseCodes = {
  UNKNOWN_ERROR: 4e3,
  UNKNOWN_OPCODE: 4001,
  DECODE_ERROR: 4002,
  NOT_AUTHENTICATED: 4003,
  AUTHENTICATION_FAILED: 4004,
  ALREADY_AUTHENTICATED: 4005,
  INVALID_SEQ: 4007,
  RATE_LIMITED: 4008,
  SESSION_TIMED_OUT: 4009,
  INVALID_API_VERSION: 4012,
  /**
   * Ours (added 2026-10-28, Console v1): maintenance is running and this
   * account is not staff. A `CONFIG_UPDATE` with the maintenance state comes
   * just before the close; reconnect after `maintenance.ends_at` (or when
   * `GET /config` says the phase is no longer `active`).
   */
  MAINTENANCE: 4100,
  /**
   * Ours (added 2026-11-02, Console v2): this desktop app is older than
   * `client.min_desktop_version`. The app shows "Update required" and does
   * not reconnect until it is updated.
   */
  UPDATE_REQUIRED: 4101
};
var NON_RESUMABLE_CLOSE_CODES = [
  GatewayCloseCodes.AUTHENTICATION_FAILED,
  GatewayCloseCodes.INVALID_API_VERSION,
  GatewayCloseCodes.UPDATE_REQUIRED
];
var PHASE1_DISPATCH_EVENTS = [
  "READY",
  "MESSAGE_CREATE",
  "MESSAGE_UPDATE",
  "MESSAGE_DELETE",
  "MESSAGE_REACTION_ADD",
  "MESSAGE_REACTION_REMOVE",
  "TYPING_START",
  "CHANNEL_CREATE",
  "CHANNEL_UPDATE",
  "CHANNEL_DELETE",
  "GUILD_MEMBER_ADD",
  "GUILD_MEMBER_UPDATE",
  "GUILD_MEMBER_REMOVE",
  "GUILD_ROLE_CREATE",
  "GUILD_ROLE_UPDATE",
  "GUILD_ROLE_DELETE",
  "PRESENCE_UPDATE",
  "VOICE_STATE_UPDATE",
  "RELATIONSHIP_ADD",
  "RELATIONSHIP_REMOVE",
  "CHANNEL_PINS_UPDATE"
];
function dispatch(t, d, s) {
  return { op: GatewayOpcodes.DISPATCH, t, s, d };
}

// src/voice.ts
var VOICE_WS_PATH = "/voice/ws";
var VOICE_RECEIVE_SLOTS = 32;
var VOICE_TOKEN_AUDIENCE = "comms-voice";
var TOKEN_ISSUER = "neosocial-server";
var VOICE_TOKEN_TTL_S = 60;
var INTERNAL_VOICE_STATE_PATH = "/internal/voice-state";
var INTERNAL_VOICE_TREE_PATH = "/internal/voice/tree";
var INTERNAL_SECRET_HEADER = "x-internal-secret";
var VoiceCloseCodes = {
  /** No `hello` in time, or a malformed first message. */
  BAD_HELLO: 4400,
  /** Token missing, invalid, expired or already used. */
  AUTH_FAILED: 4401,
  /** Not allowed into the requested channel. */
  FORBIDDEN: 4403,
  /** The same user connected again elsewhere. */
  REPLACED: 4409,
  /** Kicked or disconnected by a moderator. */
  KICKED: 4410,
  /**
   * You disconnected yourself from another device or tab (Discord's
   * Disconnect on your own voice row; bye reason `left`). Added 2026-10-28.
   */
  LEFT: 4411,
  /**
   * You no longer have access to your channel (a role or overwrite change
   * took Connect or View Channel away) and there was nowhere to move you;
   * bye reason `lost_access`. Not a moderator. Added 2026-10-28.
   */
  LOST_ACCESS: 4412,
  SERVER_ERROR: 4500
};
var INTERNAL_VOICE_TREE_CHANGED_PATH = "/internal/voice/tree-changed";
var VoiceCloseCodesExtra = {
  /** The server is shutting down (`bye` reason `shutdown`). */
  GOING_AWAY: 1001,
  /** The client sent control messages too fast (50/s, burst 100). */
  RATE_LIMITED: 4429
};
var COMMS_VOICE_REFRESH_PATH = "/internal/refresh";
var COMMS_VOICE_DISCONNECT_PATH = "/internal/voice/disconnect";
var COMMS_VOICE_MOVE_PATH = "/internal/voice/move";
var COMMS_VOICE_STATS_PATH = "/internal/stats";
var COMMS_VOICE_GRANTS_PATH = "/internal/voice/grants";
var COMMS_VOICE_MUTE_PATH = "/internal/voice/mute";
var COMMS_VOICE_CHANNEL_BAN_PATH = "/internal/voice/channel-ban";
var COMMS_VOICE_SYNC_PATH = "/internal/voice/sync";
var INTERNAL_VOICE_STATE_SYNC_PATH = "/internal/voice-state/sync";

// src/voiceMedia.ts
var VOICE_VIDEO_SLOTS = 12;
var VOICE_STREAM_AUDIO_SLOTS = 4;
var VOICE_SEND_PAUSE_MS = 3e3;
var VOICE_SLOT_LINGER_MS = 3e4;
var VOICE_PUBLISH_NEGOTIATE_MS = 15e3;
var VOICE_SDP_PER_10S = 10;
function mediaBoxWidth(height) {
  return Math.round(height * 16 / 9 / 2) * 2;
}
function fitsMediaBox(w, h, boxW, boxH) {
  return w <= boxW && h <= boxH || w <= boxH && h <= boxW;
}
function mediaScaleDown(w, h, boxW, boxH) {
  const long = Math.max(w, h);
  const short = Math.min(w, h);
  return Math.max(1, long / Math.max(boxW, boxH), short / Math.min(boxW, boxH));
}

// src/voiceExtras.ts
var NOISE_SUPPRESSION_MODES = ["off", "standard", "strong"];
var DEFAULT_NOISE_SUPPRESSION = "standard";
var PRIORITY_SPEAKER_DUCK = 0.2;
var AFK_TIMEOUTS = [60, 300, 900, 1800, 3600];
var DEFAULT_AFK_TIMEOUT = 300;
function isAfkTimeout(v) {
  return typeof v === "number" && AFK_TIMEOUTS.includes(v);
}
var VOICE_ACTIVITY_PING_MS = 15e3;
var VOICE_STATUS_MAX = 500;
function normalizeVoiceStatus(v) {
  const s = (v ?? "").trim();
  return s.length ? s : null;
}

// src/validation.ts
var USERNAME_MIN = 2;
var USERNAME_MAX = 32;
var DISPLAY_NAME_MIN = 1;
var DISPLAY_NAME_MAX = 32;
var DISPLAY_NAME_MAX_CODE_POINTS = 128;
var PASSWORD_MIN = 8;
var PASSWORD_MAX = 128;
function toAsciiDigits(input) {
  return input.replace(/[\u06F0-\u06F9\u0660-\u0669]/g, (d) => {
    const c = d.charCodeAt(0);
    return String.fromCharCode(48 + (c >= 1776 ? c - 1776 : c - 1632));
  });
}
function parseTwoStepCode(input) {
  const code = toAsciiDigits(input).replace(/[\s-]/g, "");
  if (/^\d{6}$/.test(code)) return { kind: "totp", code };
  if (/^\d{8}$/.test(code)) return { kind: "recovery", code };
  return null;
}
var RESERVED_ANYWHERE_BRAND = ["neosocial", "neoxify"];
var RESERVED_ANYWHERE_STAFF = [
  "admin",
  "moderator",
  "staff",
  "support",
  "safety",
  "security",
  "official",
  "system",
  "poshtibani"
];
var RESERVED_WHOLE_WORD = [
  "hub",
  "comms",
  "patogh",
  "bisim",
  "mod",
  "team",
  "help",
  "owner",
  "root",
  "modir",
  "nazer",
  "rasmi"
];
var RESERVED_WHOLE_NAME = [
  "everyone",
  "here",
  "null",
  "undefined",
  "api",
  "bot",
  "webhook",
  "deleted"
];
var RESERVED_USERNAMES = Object.freeze({
  anywhere: Object.freeze([...RESERVED_ANYWHERE_BRAND, ...RESERVED_ANYWHERE_STAFF]),
  wholeWord: Object.freeze([...RESERVED_WHOLE_WORD]),
  wholeName: Object.freeze([...RESERVED_WHOLE_NAME])
});
function normalizeUsername(input) {
  return toAsciiDigits(input.trim()).toLowerCase();
}
function isReservedUsername(name) {
  if (RESERVED_WHOLE_NAME.includes(name)) return true;
  if (RESERVED_USERNAMES.anywhere.some((w) => name.includes(w))) return true;
  const words = name.split(/[._0-9 ]+/).filter(Boolean);
  return words.some((w) => RESERVED_WHOLE_WORD.includes(w));
}
function validateUsername(input) {
  const value = normalizeUsername(input);
  if (value.length < USERNAME_MIN) return { ok: false, error: "too_short", value };
  if (value.length > USERNAME_MAX) return { ok: false, error: "too_long", value };
  if (!/^[a-z0-9_.]+$/.test(value)) {
    const error = /\p{L}/u.test(value.replace(/[a-z]/g, "")) ? "non_latin" : "invalid_chars";
    return { ok: false, error, value };
  }
  if (value.includes("..")) return { ok: false, error: "consecutive_dots", value };
  if (isReservedUsername(value)) return { ok: false, error: "reserved", value };
  return { ok: true, value };
}
var BIDI_CONTROLS = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
var INVISIBLES = /[\u00AD\u034F\u115F\u1160\u17B4\u17B5\u180B-\u180F\u200B\u2060-\u2064\u206A-\u206F\u3164\uFEFF\uFFA0]|\uD834[\uDD73-\uDD7A]|\uDB40[\uDC00-\uDCFF]/g;
var BLANK_RENDERING = /[\s\u2800\u3164\u115F\u1160\uFFA0\u200C\u200D\uFE0E\uFE0F]/gu;
var LINE_BREAKS_AND_CONTROLS = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]+/g;
var ARABIC_LETTER = /[\p{Script=Arabic}\p{Script=Syriac}\p{Script=Nko}]/u;
var PICTOGRAPHIC2 = /[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\uFE0F\u20E3]/u;
function graphemeCount(s) {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    let n = 0;
    for (const _ of new Intl.Segmenter(void 0, { granularity: "grapheme" }).segment(s)) n += 1;
    return n;
  }
  return [...s].length;
}
function sanitizeDisplayName(input) {
  let s = input.normalize("NFC").replace(LINE_BREAKS_AND_CONTROLS, " ");
  s = s.replace(BIDI_CONTROLS, "").replace(INVISIBLES, "");
  const cps = [...s];
  const out = [];
  for (let i = 0; i < cps.length; i++) {
    const c = cps[i];
    if (c === "\u200C" || c === "\u200D") {
      const prev = out[out.length - 1] ?? "";
      const next = cps[i + 1] ?? "";
      const keep = c === "\u200C" ? ARABIC_LETTER.test(prev) && ARABIC_LETTER.test(next) : PICTOGRAPHIC2.test(prev) && PICTOGRAPHIC2.test(next);
      if (keep && prev !== c) out.push(c);
      continue;
    }
    out.push(c);
  }
  return out.join("").replace(/\s+/gu, " ").trim();
}
function validateDisplayName(input) {
  const value = sanitizeDisplayName(input);
  if (value.length === 0) return { ok: false, error: "empty", value };
  if (graphemeCount(value) > DISPLAY_NAME_MAX || [...value].length > DISPLAY_NAME_MAX_CODE_POINTS) {
    return { ok: false, error: "too_long", value };
  }
  if (value.replace(BLANK_RENDERING, "").length === 0) return { ok: false, error: "blank", value };
  return { ok: true, value };
}
function displayNameLength(input) {
  return graphemeCount(sanitizeDisplayName(input));
}
function validatePassword(input) {
  const n = [...input].length;
  if (n < PASSWORD_MIN) return { ok: false, error: "too_short", value: input };
  if (n > PASSWORD_MAX) return { ok: false, error: "too_long", value: input };
  return { ok: true, value: input };
}
var EMAIL_MAX = 254;
var EMAIL_LOCAL_MAX = 64;
function normalizeEmail(input) {
  return toAsciiDigits(input).trim().toLowerCase();
}
function validateEmail(input) {
  const value = normalizeEmail(input);
  if (value.length > EMAIL_MAX) return { ok: false, error: "too_long", value };
  const bad = { ok: false, error: "invalid", value };
  const at = value.indexOf("@");
  if (at < 1 || at !== value.lastIndexOf("@")) return bad;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (local.length > EMAIL_LOCAL_MAX) return bad;
  if (!/^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+$/.test(local)) return bad;
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) return bad;
  const labels = domain.split(".");
  if (labels.length < 2) return bad;
  for (const l of labels) {
    if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(l)) return bad;
  }
  const tld = labels[labels.length - 1];
  if (!/^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/.test(tld)) return bad;
  return { ok: true, value };
}
function looksLikeEmail(login) {
  return login.includes("@");
}
function maskEmail(email) {
  const at = email.lastIndexOf("@");
  if (at < 1) return "***";
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf(".");
  const host = dot > 0 ? domain.slice(0, dot) : domain;
  const tld = dot > 0 ? domain.slice(dot) : "";
  return `${local[0]}***@${host[0] ?? ""}***${tld}`;
}

// src/search.ts
function normalizeForSearch(input) {
  return input.replace(INVISIBLE, "").normalize("NFKC").replace(BLANK, " ").replace(/[\u064A\u0649\u0626]/g, "\u06CC").replace(/\u0643/g, "\u06A9").replace(/[\u0629\u06C0\u06D5]/g, "\u0647").replace(/[\u0622\u0623\u0625\u0671]/g, "\u0627").replace(/\u0624/g, "\u0648").replace(/[\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g, "").replace(/[\u06F0-\u06F9\u0660-\u0669]/g, (d) => {
    const c = d.charCodeAt(0);
    return String.fromCharCode(48 + (c >= 1776 ? c - 1776 : c - 1632));
  }).toLowerCase().replace(/\s+/gu, " ").trim();
}
var INVISIBLE = /[\p{Cf}͏︀-️\u{E0100}-\u{E01EF}]/gu;
var BLANK = /[ᅟᅠㅤﾠ⠀]/g;
function escapeLikePattern(input) {
  let out = "";
  for (const ch of input) {
    out += ch === "%" || ch === "_" || ch === BACKSLASH ? BACKSLASH + ch : ch;
  }
  return out;
}
var BACKSLASH = String.fromCharCode(92);
function searchPattern(query) {
  return `%${escapeLikePattern(normalizeForSearch(query))}%`;
}

// src/dates.ts
var MINIMUM_AGE = 18;
var ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseIsoDate(value) {
  const m = ISO_DATE_RE.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const t = new Date(Date.UTC(year, month - 1, day));
  if (t.getUTCFullYear() !== year || t.getUTCMonth() !== month - 1 || t.getUTCDate() !== day) {
    return null;
  }
  return { year, month, day };
}
function formatIsoDate(d) {
  const p = (n, w) => String(n).padStart(w, "0");
  return `${p(d.year, 4)}-${p(d.month, 2)}-${p(d.day, 2)}`;
}
function ageOn(birth, now = /* @__PURE__ */ new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const d = now.getUTCDate();
  let age = y - birth.year;
  if (m < birth.month || m === birth.month && d < birth.day) age -= 1;
  return age;
}
function validateBirthDate(value, now = /* @__PURE__ */ new Date()) {
  const d = parseIsoDate(value);
  if (!d || d.year < 1900) return { ok: false, error: "invalid" };
  if (Date.UTC(d.year, d.month - 1, d.day) > now.getTime()) return { ok: false, error: "future" };
  if (ageOn(d, now) < MINIMUM_AGE) return { ok: false, error: "underage" };
  return { ok: true, value: formatIsoDate(d) };
}

// src/desktop.ts
var DESKTOP_BRIDGE_VERSION = 1;
var DesktopEvents = {
  ptt: "neosocial:ptt",
  trayMute: "neosocial:tray-mute",
  update: "neosocial:update",
  notificationClick: "neosocial:notification-click"
};
function mouseButtonToPttCode(button) {
  switch (button) {
    case 1:
      return "MouseMiddle";
    case 3:
      return "MouseBack";
    case 4:
      return "MouseForward";
    default:
      return null;
  }
}
var DESKTOP_GAME_EVENT = "neosocial:game";
function getDesktopBridge() {
  const w = globalThis.neosocialDesktop;
  return w && w.isDesktop === true ? w : null;
}
var DESKTOP_BRIDGE_GLOBAL = "neosocialDesktop";
var DESKTOP_UPDATER_PUBKEY = "dW50cnVzdGVkIGNvbW1lbnQ6IG1pbmlzaWduIHB1YmxpYyBrZXk6IEE3MDJCMjlBNkIwMTUzNjYKUldSbVV3RnJtcklDcDlZcnd6M2wxL1phTHZRYmtpc0d3NlU2QVR0T0dxUEdQNURVL1hGN0NBWDkK";

// src/usermgmt.ts
var RESTORE_WINDOW_MS = 7 * 24 * 60 * 60 * 1e3;

// src/images.ts
var KiB = 1024;
var MiB = 1024 * 1024;
var IMAGE_LIMITS = {
  avatar: {
    maxBytes: 2 * MiB,
    targetBytes: 512 * KiB,
    maxWidth: 512,
    maxHeight: 512,
    maxPixels: 512 * 512,
    maxInputPixels: 25e6,
    animated: false
  },
  icon: {
    maxBytes: 2 * MiB,
    targetBytes: 512 * KiB,
    maxWidth: 512,
    maxHeight: 512,
    maxPixels: 512 * 512,
    maxInputPixels: 25e6,
    animated: false
  },
  /** Profile and server banners are 5:2, like the profile card's art. */
  banner: {
    maxBytes: 4 * MiB,
    targetBytes: 1 * MiB,
    maxWidth: 1600,
    maxHeight: 640,
    maxPixels: 1600 * 640,
    maxInputPixels: 25e6,
    animated: false
  },
  emoji: {
    maxBytes: 256 * KiB,
    targetBytes: 128 * KiB,
    maxWidth: 128,
    maxHeight: 128,
    maxPixels: 128 * 128,
    maxInputPixels: 4e6,
    animated: true
  },
  /**
   * A scheduled event's cover (2026-10-29): 5:2 like a banner, at Discord's
   * 800 x 320, so the events list stays light on the server's traffic (a
   * cover is a few tens of KB).
   */
  event_cover: {
    maxBytes: 1 * MiB,
    targetBytes: 160 * KiB,
    maxWidth: 800,
    maxHeight: 320,
    maxPixels: 800 * 320,
    maxInputPixels: 25e6,
    animated: false
  },
  /**
   * Chat images: at most 16.7 megapixels (4096 x 4096 worth) and 16384 a
   * side. The client never sends more than that, so 25 megapixels in is
   * room enough, and it keeps one decode under about 100 MB of memory.
   */
  attachment: {
    maxBytes: 8 * MiB,
    targetBytes: 4 * MiB,
    maxWidth: 16384,
    maxHeight: 16384,
    maxPixels: 4096 * 4096,
    maxInputPixels: 25e6,
    animated: true
  }
};
var MAX_ANIMATION_PIXELS = 5e7;
var MAX_ANIMATION_FRAMES = 1e3;
var BANNER_ASPECT = 5 / 2;
var MIN_UPLOAD_IMAGE_BYTES = 512 * KiB;
var MAX_UPLOAD_IMAGE_BYTES = IMAGE_LIMITS.attachment.maxBytes;
var MAX_ATTACHMENTS_PER_MESSAGE = 10;
var clampInt = (v, min, max, fallback) => typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.floor(v))) : fallback;
function chatImageBytes(uploadImageBytes) {
  const maxBytes = clampInt(
    uploadImageBytes,
    MIN_UPLOAD_IMAGE_BYTES,
    MAX_UPLOAD_IMAGE_BYTES,
    MAX_UPLOAD_IMAGE_BYTES
  );
  return {
    maxBytes,
    targetBytes: Math.min(IMAGE_LIMITS.attachment.targetBytes, Math.floor(maxBytes / 2))
  };
}
function attachmentsPerMessage(setting) {
  return clampInt(setting, 1, MAX_ATTACHMENTS_PER_MESSAGE, MAX_ATTACHMENTS_PER_MESSAGE);
}

// src/admin.ts
var STAFF_PERMISSIONS = [
  "users.read",
  "users.read_private",
  "users.edit",
  "users.suspend",
  "users.delete",
  "users.reset_2fa",
  "usernames.manage",
  "signup.manage",
  "invites.manage",
  "community.manage",
  "moderation.reports",
  "moderation.actions",
  "moderation.appeals",
  "moderation.filters",
  "content.read",
  "content.delete",
  /**
   * Console v2 (2026-11-02, decision 3): reading any direct or group
   * message content, reported or not (decision 5, 2026-11-03). Only the
   * Owner has it by default; the Owner grants it to a staff member
   * (`staff_members.grants`), never through a role.
   */
  "content.read_dms",
  "voice.manage",
  "features.manage",
  "settings.manage",
  "releases.manage",
  "analytics.read",
  "system.read",
  "system.manage",
  "staff.manage",
  "audit.read"
];
function isStaffPermissionKey(value) {
  return typeof value === "string" && STAFF_PERMISSIONS.includes(value);
}
var ALL2 = STAFF_PERMISSIONS;
var except = (...keys) => ALL2.filter((k) => !keys.includes(k));
var BUILTIN_STAFF_ROLES = Object.freeze({
  owner: { id: "owner", name: "Owner", builtin: true, permissions: [...ALL2] },
  admin: {
    id: "admin",
    name: "Admin",
    builtin: true,
    permissions: except("staff.manage", "content.read_dms")
  },
  trust_safety: {
    id: "trust_safety",
    name: "Trust & Safety",
    builtin: true,
    // Reports, cases, appeals; moderate users and content; read the user
    // details a case needs (every reveal is audited with a reason).
    permissions: [
      "users.read",
      "users.read_private",
      "users.edit",
      "users.suspend",
      "moderation.reports",
      "moderation.actions",
      "moderation.appeals",
      "moderation.filters",
      "content.read",
      "content.delete"
    ]
  },
  support: {
    id: "support",
    name: "Support",
    builtin: true,
    // Look up users; reset two-step through the recovery procedure; unlock;
    // sign out a hacked account's devices. No bans, no content access.
    permissions: ["users.read", "users.reset_2fa"]
  },
  analyst: {
    id: "analyst",
    name: "Analyst",
    builtin: true,
    permissions: ["analytics.read"]
  },
  auditor: {
    id: "auditor",
    name: "Auditor",
    builtin: true,
    permissions: ["audit.read"]
  }
});
function isBuiltinStaffRoleId(value) {
  return typeof value === "string" && Object.hasOwn(BUILTIN_STAFF_ROLES, value);
}
function hasStaffPermission(granted, key) {
  return granted.includes(key);
}
function hasAnyStaffPermission(granted, anyOf) {
  return anyOf.some((k) => granted.includes(k));
}
var FEATURE_FLAG_DEFAULTS = Object.freeze({
  signups: true,
  dms: true,
  group_dms: true,
  friend_requests: true,
  voice: true,
  pokes: true,
  image_uploads: true,
  message_edit: true,
  reactions: true,
  /** Custom server emoji (emoji.ts), added 2026-11-01. */
  custom_emoji: true,
  /**
   * Who reacted (2026-11-01): the reaction tooltip ("👍 reacted by A, B
   * and 3 others") and the "View reactions" list. Off: the apps show
   * counts only, and the server refuses `GET .../reactions/{emoji}`.
   */
  reaction_details: true,
  pins: true,
  search: true,
  custom_status: true,
  web_client: true,
  desktop_client: true,
  desktop_global_ptt: true,
  /** Threads (2026-10-29): public and private threads in text channels. */
  threads: true,
  /** Polls (2026-10-29): off, the apps hide "Create poll" and the server refuses new polls and votes. */
  polls: true,
  /** Forwarding messages (2026-10-29): off, the apps hide Forward and the server refuses forwards. */
  message_forwarding: true,
  /** Notification settings and mute (2026-10-29): off, everyone gets the server's defaults. */
  notifications: true,
  /**
   * AutoMod (2026-10-29). Off: rules check nothing (resolved for the
   * message's author) and the rules API answers FEATURE_DISABLED.
   */
  automod: true,
  /**
   * Slowmode (2026-10-29). Off: no channel slows anyone (resolved for the
   * sender) and a channel can't be given slowmode.
   */
  slowmode: true,
  /** Scheduled events (2026-10-29, events.ts). */
  scheduled_events: true,
  // ---- the social feature, added 2026-10-29 (social.ts) ----
  /** "Playing X": the desktop app's game detection and its activity on presences. */
  rich_presence: true,
  /** Private notes on profiles. */
  user_notes: true,
  /** Settings > Account: Disable account and Delete account. */
  account_removal: true,
  /**
   * The web app's "Get the Neosocial app for Windows" banner (2026-11-01),
   * shown only on a Windows desktop browser and only once a release is
   * published at /download/latest.json.
   */
  desktop_download_banner: true,
  /**
   * Camera video in voice (2026-10-29). Built, off by default: the camera
   * button says "Coming soon" until the owner turns it on. Gates publishing
   * only; anyone in the channel can watch.
   */
  video: false,
  /** Screen share (Go Live) in voice (2026-10-29). Gates publishing only. */
  screen_share: true,
  /**
   * The stream pop-out (2026-11-01, Discord's): a stream you watch stays in
   * a small player while its voice room is away. Off: a stream shows in its
   * room only (its sound still plays while you watch). Resolved per user by
   * the server like every flag; comms-voice still sends a video only while
   * it is watched and on screen.
   */
  stream_popout: true,
  /**
   * Voice calls in DMs and group DMs (2026-11-01): the Call button, the ring
   * and the call (with `voice` on too). Turning it off ends running calls.
   */
  dm_calls: true,
  /** Noise suppression's Strong option (RNNoise in the app, 2026-11-01). Off: Standard only. */
  noise_suppression: true,
  /** Priority speaker (Discord's PRIORITY_SPEAKER, 2026-11-01). Off: granted nowhere. */
  priority_speaker: true,
  /** AFK auto-move (2026-11-01): idle members moved to the server's AFK channel. */
  afk_move: true,
  /** Voice channel status (Discord's SET_VOICE_CHANNEL_STATUS, 2026-11-01). */
  voice_channel_status: true,
  voice_regions: false,
  maintenance_mode: false
});
var FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAG_DEFAULTS);
var FEATURE_FLAGS_NOT_BUILT = [];
var FEATURE_FLAGS_HIDDEN = ["voice_regions"];
var FEATURE_FLAGS_GLOBAL_ONLY = ["signups", "maintenance_mode"];
var FLAG_KEY_PATTERN = /^[a-z][a-z0-9_]{1,47}$/;
function isFeatureFlagKey(value) {
  return typeof value === "string" && Object.hasOwn(FEATURE_FLAG_DEFAULTS, value);
}
var RETIRED_FEATURE_FLAGS = [
  "hub_mode",
  "comms_mode",
  "whisper",
  "channel_commander",
  // the owner's rule (2026-11-02): link previews are never fetched
  "link_previews"
];
function isRetiredFlagKey(value) {
  return typeof value === "string" && RETIRED_FEATURE_FLAGS.includes(value);
}
function fnv1a32(input) {
  let h = 2166136261;
  for (const byte of new TextEncoder().encode(input)) {
    h ^= byte;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
function rolloutBucket(flagKey, userId) {
  return fnv1a32(`${flagKey}:${userId}`) % 100;
}
function defaultFeatureFlag(key) {
  return {
    key,
    enabled: FEATURE_FLAG_DEFAULTS[key],
    killed: false,
    role_ids: [],
    user_ids: [],
    rollout_percent: 0,
    builtin: true,
    description: "",
    updated_at: null,
    updated_by: null
  };
}
function resolveFlag(flag, subject) {
  if (flag.killed) return false;
  if (flag.enabled) return true;
  if (!subject) return false;
  if (FEATURE_FLAGS_GLOBAL_ONLY.includes(flag.key)) return false;
  if (flag.user_ids.includes(subject.user_id)) return true;
  if (flag.role_ids.some((r) => subject.role_ids.includes(r))) return true;
  const pct = Math.max(0, Math.min(100, Math.floor(flag.rollout_percent)));
  return pct > 0 && rolloutBucket(flag.key, subject.user_id) < pct;
}
function resolveFlags(stored, subject) {
  const byKey = new Map(stored.map((f) => [f.key, f]));
  const out = {};
  for (const key of FEATURE_FLAG_KEYS) {
    out[key] = resolveFlag(byKey.get(key) ?? defaultFeatureFlag(key), subject);
  }
  for (const f of stored) if (!(f.key in out)) out[f.key] = resolveFlag(f, subject);
  return out;
}
var SERVER_CREATION_MODES = ["off", "staff", "everyone"];
var STAFF_REVIEW_MARKER = "<!-- staff-review -->";
var STAFF_REVIEW_CLAUSE = "Neosocial staff may review messages, including direct and group messages, and uploaded media, to keep the service safe and enforce these terms. Every review is recorded with the staff member's name and reason.";
var STAFF_REVIEW_TEXT = `${STAFF_REVIEW_MARKER}
${STAFF_REVIEW_CLAUSE}`;
function visibleConsoleText(text2) {
  return text2.replace(/<!--[\s\S]*?-->\n?/g, "").trim();
}
var int = (d, min, max, pub = true) => ({ kind: "int", default: d, min, max, public: pub });
var text = (en, max_length = 4e3) => ({ kind: "text", default: { en }, public: true, max_length });
var rate = (count, window_s) => ({ kind: "rate", default: { count, window_s }, min: 1, max: 86400, public: true });
var APP_SETTING_DEFS = Object.freeze({
  "limits.message_length": int(2e3, 1, 4e3),
  "limits.upload_image_bytes": int(
    MAX_UPLOAD_IMAGE_BYTES,
    MIN_UPLOAD_IMAGE_BYTES,
    MAX_UPLOAD_IMAGE_BYTES
  ),
  "limits.attachments_per_message": int(
    MAX_ATTACHMENTS_PER_MESSAGE,
    1,
    MAX_ATTACHMENTS_PER_MESSAGE
  ),
  "limits.group_dm_size": int(10, 2, 25),
  "limits.friends": int(1e3, 1, 5e3),
  "limits.rate_messages": rate(5, 5),
  "limits.rate_joins": rate(10, 60),
  "limits.rate_friend_requests": rate(50, 86400),
  "limits.voice_bitrate_kbps": int(64, 8, 256),
  "limits.voice_receive_slots": int(32, 1, 32),
  // Discord's free tier (720p30); docs/decisions/2026-10-29-screen-share-and-video.md §2.2.
  "limits.stream_bitrate_kbps": int(1500, 300, 8e3),
  "limits.stream_max_height": int(720, 360, 1080),
  "limits.stream_max_fps": int(30, 5, 60),
  "limits.stream_audio_kbps": int(64, 16, 128),
  "limits.camera_bitrate_kbps": int(350, 100, 2500),
  "limits.camera_max_height": int(360, 180, 720),
  "limits.camera_max_fps": int(24, 5, 30),
  "limits.streams_per_channel": int(2, 1, 4),
  "limits.cameras_per_channel": int(4, 1, 8),
  "limits.active_threads": int(1e3, 1, 1e3),
  "limits.automod_keywords": int(1e3, 10, 1e3),
  "limits.scheduled_events": int(100, 1, 100),
  "limits.guilds_per_user": int(100, 1, 200),
  "limits.guilds_created_per_day": int(10, 1, 100),
  "texts.app_name": text("Neosocial", 40),
  "texts.welcome": text("Welcome to Neosocial."),
  "texts.rules": text("", 2e4),
  "texts.data_notice": text("", 2e4),
  // the staff-review clause (Console v2 decision 13) stays editable
  "texts.terms": text(STAFF_REVIEW_TEXT, 1e5),
  "texts.privacy": text(STAFF_REVIEW_TEXT, 1e5),
  "texts.signup_open": text(""),
  "texts.signup_invite_only": text(
    "Neosocial is invite-only for now. You need an invite code to sign up."
  ),
  "texts.signup_closed": text("Sign-up is closed right now."),
  "texts.maintenance": text("Neosocial is down for maintenance. We\u2019ll be back soon."),
  "texts.raid_signups": text("Sign-up is paused for a short while. Please try again later."),
  "defaults.theme": {
    kind: "enum",
    default: "system",
    options: ["system", "pearl", "smoked"],
    public: true
  },
  "defaults.locale": { kind: "enum", default: "en", options: ["en"], public: true },
  "defaults.notification_level": {
    kind: "enum",
    default: "mentions",
    options: ["all", "mentions", "nothing"],
    public: true
  },
  "defaults.voice_mode": {
    kind: "enum",
    default: "voice_activity",
    options: ["voice_activity", "push_to_talk"],
    public: true
  },
  "display.role_icons": {
    kind: "enum",
    default: "highest",
    options: ROLE_ICON_MODES,
    public: true
  },
  "signup.mode": {
    kind: "enum",
    default: "invite_only",
    options: ["open", "invite_only", "closed"],
    public: true
  },
  "signup.min_age": { ...int(18, 18, 18), readonly: true },
  "signup.pow_difficulty": int(0, 0, 32, false),
  "signup.per_network_per_hour": int(10, 1, 1e3, false),
  "usernames.unused_release_days": int(90, 30, 3650, false),
  "usernames.deleted_hold_days": int(14, 1, 365, false),
  "console.ip_allowlist": { kind: "cidr_list", default: [], public: false },
  "community.server_creation": {
    kind: "enum",
    default: "staff",
    options: SERVER_CREATION_MODES,
    public: true
  },
  "raid.default_minutes": int(60, 15, 1440, false),
  "raid.new_account_hours": int(24, 1, 720, false),
  "raid.slowmode_s": int(10, 1, 21600, false),
  "client.min_desktop_version": { kind: "version", default: "", public: true }
});
var APP_VERSION_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;
function compareAppVersions(a, b) {
  const pa = a.split(".").map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}
var APP_SETTING_KEYS = Object.keys(APP_SETTING_DEFS);
var PUBLIC_APP_SETTING_KEYS = APP_SETTING_KEYS.filter(
  (k) => APP_SETTING_DEFS[k].public
);
function defaultAppSettings() {
  const out = {};
  for (const k of APP_SETTING_KEYS) out[k] = structuredClone(APP_SETTING_DEFS[k].default);
  return out;
}
function publicAppSettings(settings) {
  const out = {};
  for (const k of PUBLIC_APP_SETTING_KEYS) out[k] = settings[k];
  return out;
}
var CIDR4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/;
var CIDR6 = /^[0-9a-f:]+\/(\d{1,3})$/i;
function isCidr(v) {
  const m4 = CIDR4.exec(v);
  if (m4) return m4.slice(1, 5).every((o) => Number(o) <= 255) && Number(m4[5]) <= 32;
  const m6 = CIDR6.exec(v);
  return !!m6 && v.includes(":") && Number(m6[1]) <= 128;
}
function validateAppSetting(key, value) {
  if (!Object.hasOwn(APP_SETTING_DEFS, key)) return "UNKNOWN_SETTING";
  const def = APP_SETTING_DEFS[key];
  if (def.readonly) return "READ_ONLY";
  const num = (n) => {
    if (typeof n !== "number" || !Number.isInteger(n)) return "BASE_TYPE_BAD_TYPE";
    if (def.min !== void 0 && n < def.min) return "NUMBER_TYPE_MIN";
    if (def.max !== void 0 && n > def.max) return "NUMBER_TYPE_MAX";
    return null;
  };
  switch (def.kind) {
    case "int":
      return num(value);
    case "bool":
      return typeof value === "boolean" ? null : "BASE_TYPE_BAD_TYPE";
    case "enum":
      if (typeof value !== "string") return "BASE_TYPE_BAD_TYPE";
      return def.options?.includes(value) ? null : "BASE_TYPE_CHOICES";
    case "text": {
      if (typeof value !== "object" || value === null) return "BASE_TYPE_BAD_TYPE";
      const t = value;
      if (typeof t.en !== "string") return "BASE_TYPE_BAD_TYPE";
      return t.en.length > (def.max_length ?? Infinity) ? "BASE_TYPE_MAX_LENGTH" : null;
    }
    case "rate": {
      if (typeof value !== "object" || value === null) return "BASE_TYPE_BAD_TYPE";
      const r = value;
      return num(r.count) ?? num(r.window_s);
    }
    case "cidr_list":
      if (!Array.isArray(value) || value.some((v) => typeof v !== "string")) {
        return "BASE_TYPE_BAD_TYPE";
      }
      return value.every((v) => isCidr(v)) ? null : "CIDR_INVALID";
    case "version":
      if (typeof value !== "string") return "BASE_TYPE_BAD_TYPE";
      return value === "" || APP_VERSION_PATTERN.test(value) ? null : "BASE_TYPE_CHOICES";
  }
}
function effectiveSignupMode(signupsFlagOn, mode, raidActive2 = false) {
  return signupsFlagOn && !raidActive2 ? mode : "closed";
}
var DEFAULT_MAINTENANCE = Object.freeze({
  enabled: false,
  starts_at: null,
  ends_at: null,
  message: null,
  warn_minutes: 5
});
function maintenancePhase(state, nowMs) {
  if (!state.enabled) return "off";
  if (!state.starts_at) return "active";
  const start = Date.parse(state.starts_at);
  if (nowMs >= start) return "active";
  return nowMs >= start - state.warn_minutes * 6e4 ? "warning" : "scheduled";
}
function isAnnouncementActive(a, nowMs, roleIds) {
  if (nowMs < Date.parse(a.starts_at)) return false;
  if (a.ends_at && nowMs >= Date.parse(a.ends_at)) return false;
  if (a.role_ids.length === 0) return true;
  return !!roleIds && a.role_ids.some((r) => roleIds.includes(r));
}
var CLIENT_CONFIG_PATH = "/config";
function isNewerConfig(current, incoming) {
  return !current || incoming.version > current.version;
}
var ADMIN_API_BASE = "/admin-api/v1";
var ADMIN_SESSION_IDLE_MS = 30 * 60 * 1e3;
var ADMIN_SESSION_MAX_MS = 8 * 60 * 60 * 1e3;
var ADMIN_REASON_MIN = 3;
var ADMIN_REASON_MAX = 512;
var ADMIN_PAGE_DEFAULT = 50;
var ADMIN_PAGE_MAX = 200;
var ae = encodeURIComponent;
var AdminRoutes = {
  // session
  session: () => "/session",
  // POST sign in (step 1), DELETE sign out
  sessionMfa: () => "/session/mfa",
  // POST step 2
  me: () => "/me",
  // overview
  overviewStats: () => "/overview/stats",
  overviewHealth: () => "/overview/health",
  // users
  users: () => "/users",
  user: (id) => `/users/${id}`,
  userTab: (id, tab) => `/users/${id}/${tab}`,
  userReveal: (id) => `/users/${id}/reveal`,
  userSession: (id, sessionId) => `/users/${id}/sessions/${ae(sessionId)}`,
  userActions: (id) => `/users/${id}/actions`,
  // usernames
  reservedNames: () => "/usernames/reserved",
  reservedName: (id) => `/usernames/reserved/${id}`,
  confusables: () => "/usernames/confusables",
  // sign-up and invites
  signup: () => "/signup",
  signupMode: () => "/signup/mode",
  signupRules: () => "/signup/rules",
  signupFunnel: () => "/signup/funnel",
  inviteBatches: () => "/invites/batches",
  inviteBatch: (id) => `/invites/batches/${id}`,
  inviteBatchRevoke: (id) => `/invites/batches/${id}/revoke`,
  inviteCodes: () => "/invites/codes",
  inviteCode: (code) => `/invites/codes/${ae(code)}`,
  inviteCodeRevoke: (code) => `/invites/codes/${ae(code)}/revoke`,
  // features, settings, announcements, maintenance
  flags: () => "/flags",
  flag: (key) => `/flags/${ae(key)}`,
  settings: () => "/settings",
  announcements: () => "/announcements",
  announcement: (id) => `/announcements/${id}`,
  maintenance: () => "/maintenance",
  // system
  systemServices: () => "/system/services",
  systemResources: () => "/system/resources",
  systemBackups: () => "/system/backups",
  // GET list, POST run now
  systemTls: () => "/system/tls",
  systemErrors: () => "/system/errors",
  // staff and audit
  staff: () => "/staff",
  staffRoles: () => "/staff/roles",
  staffMember: (userId) => `/staff/${userId}`,
  audit: () => "/audit",
  auditVerify: () => "/audit/verify"
};
var DISK_ALERT_PERCENT = 80;
var TLS_ALERT_DAYS = 14;
var USER_WARNINGS_PATH = "/users/@me/warnings";
var VIOLATION_WEIGHTS = Object.freeze({
  low: 1,
  medium: 2,
  high: 3
});
function standingFor(activeSeverities, suspended) {
  if (suspended) return "suspended";
  const sum = activeSeverities.reduce((s, v) => s + VIOLATION_WEIGHTS[v], 0);
  if (sum === 0) return "all_good";
  if (sum <= 2) return "limited";
  if (sum <= 4) return "very_limited";
  return "at_risk";
}
var DEFAULT_LIMIT_FEATURES = [
  "dm_non_friends",
  "create_invites",
  "links_files"
];
var SUSPEND_PRESET_DAYS = [1, 3, 7];
var SUSPEND_MAX_DAYS = 365;
var ADMIN_SENSITIVE_USER_ACTIONS = [
  "suspend",
  "disable",
  "reset_mfa",
  "force_rename",
  "export",
  "delete",
  // A verified email can reset the password, so vouching for one is sensitive.
  "mark_verified"
];
var ADMIN_USER_ACTION_PERMISSIONS = Object.freeze({
  warn: ["users.suspend", "moderation.actions"],
  limit: ["users.suspend"],
  suspend: ["users.suspend"],
  disable: ["users.suspend"],
  enable: ["users.suspend"],
  sign_out_all: ["users.edit", "users.reset_2fa"],
  reset_mfa: ["users.reset_2fa"],
  unlock: ["users.edit", "users.reset_2fa"],
  force_rename: ["users.edit", "usernames.manage"],
  reset_profile: ["users.edit"],
  export: ["users.read_private"],
  delete: ["users.delete"],
  cancel_deletion: ["users.delete"],
  resend_verification: ["users.edit", "users.reset_2fa"],
  mark_verified: ["users.edit"]
});
function canDoUserAction(granted, action) {
  return hasAnyStaffPermission(granted, ADMIN_USER_ACTION_PERMISSIONS[action]);
}
var RESERVED_NAME_CATEGORIES = [
  "brand",
  "staff",
  "system",
  "held"
];
var FOLD_ARABIC_SCRIPT = {
  \u064A: "\u06CC",
  \u0649: "\u06CC",
  \u0626: "\u06CC",
  \u0643: "\u06A9",
  \u0629: "\u0647",
  \u06C0: "\u0647",
  \u06D5: "\u0647",
  \u0623: "\u0627",
  \u0625: "\u0627",
  \u0622: "\u0627",
  \u0671: "\u0627",
  \u0624: "\u0648"
};
var FOLD_UPPER = {
  \u0391: "a",
  \u0392: "b",
  \u0395: "e",
  \u0396: "z",
  \u0397: "h",
  \u0399: "l",
  \u039A: "k",
  \u039C: "m",
  \u039D: "n",
  \u039F: "o",
  \u03A1: "p",
  \u03A4: "t",
  \u03A5: "y",
  \u03A7: "x",
  \u0410: "a",
  \u0412: "b",
  \u0415: "e",
  \u041A: "k",
  \u041C: "m",
  \u041D: "h",
  \u041E: "o",
  \u0420: "p",
  \u0421: "c",
  \u0422: "t",
  \u0425: "x"
};
var FOLD_LATIN = {
  "0": "o",
  "1": "l",
  i: "l",
  "|": "l",
  "!": "l",
  "3": "e",
  "4": "a",
  "5": "s",
  "7": "t",
  "8": "b",
  "9": "g",
  $: "s",
  "@": "a",
  \u0430: "a",
  \u0435: "e",
  \u043E: "o",
  \u0440: "p",
  \u0441: "c",
  \u0443: "y",
  \u0445: "x",
  \u043A: "k",
  \u043C: "m",
  \u0442: "t",
  \u0432: "b",
  \u043D: "h",
  \u0456: "l",
  \u0458: "j",
  \u03BF: "o",
  \u03B1: "a",
  \u03B5: "e",
  \u03B9: "l",
  \u03BA: "k",
  \u03BD: "v",
  \u03C1: "p",
  \u03C4: "t",
  \u03C5: "u"
};
function confusableSkeleton(input) {
  let s = "";
  for (const ch of input.normalize("NFKC")) s += FOLD_UPPER[ch] ?? ch;
  s = s.toLowerCase();
  s = s.replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 1776));
  s = s.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 1632));
  s = s.normalize("NFD").replace(/[\u0300-\u036F\u064B-\u065F\u0670]/g, "");
  s = s.replace(/[\s._\-\u200C\u0640]|\u200D/g, "");
  let out = "";
  for (const ch of s) out += FOLD_ARABIC_SCRIPT[ch] ?? FOLD_LATIN[ch] ?? ch;
  return out.replace(/rn/g, "m").replace(/vv/g, "w");
}
function matchesReservedName(candidate, entry) {
  const target = confusableSkeleton(entry.name);
  if (!target) return false;
  if (entry.match === "contains") return confusableSkeleton(candidate).includes(target);
  if (entry.match === "exact") return confusableSkeleton(candidate) === target;
  const words = candidate.normalize("NFKC").replace(/([a-z])([A-Z])/g, "$1 $2").split(/[\s._\-\u200C]+/).map(confusableSkeleton).filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    let run = "";
    for (let j = i; j < words.length && run.length < target.length; j++) {
      run += words[j];
      if (run === target) return true;
    }
  }
  return false;
}
function inviteBatchState(batch, codeStates) {
  if (codeStates.includes("live")) return "live";
  if (batch.revoked_at || codeStates.includes("revoked")) return "revoked";
  if (codeStates.includes("expired")) return "expired";
  return "used_up";
}
var INVITE_BATCH_MAX = 500;
function parseAuditActionFilter(filter) {
  const parts = filter.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 20);
  return {
    exact: parts.filter((x) => !x.endsWith(".")),
    prefixes: parts.filter((x) => x.endsWith("."))
  };
}
function auditActionMatches(action, filter) {
  const f = parseAuditActionFilter(filter);
  if (!f.exact.length && !f.prefixes.length) return true;
  return f.exact.includes(action) || f.prefixes.some((p) => action.startsWith(p));
}
var AUDIT_ACTION_GROUPS = {
  user: "user.,users.",
  flag: "flag.,flags.",
  settings: "settings.",
  signup: "signup.",
  invites: "invite_batch.,invite_batches.,invite_code.,invite_codes.",
  announcement: "announcement.,announcements.",
  maintenance: "maintenance.",
  staff: "staff.,access.",
  session: "session.,me.",
  system: "system.",
  reserved_name: "reserved_name.,reserved_names.,usernames.",
  audit: "audit.",
  // Console v2 (2026-11-02)
  community: "community.",
  moderation: "moderation.",
  content: "content.",
  voice: "voice.",
  releases: "releases.",
  analytics: "analytics.",
  approval: "approval."
};
var AUDIT_GENESIS_HASH = "0".repeat(64);
function canonicalJson(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const obj = value;
  const keys = Object.keys(obj).filter((k) => obj[k] !== void 0).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`;
}
function auditHashInput(e2) {
  const { prev_hash, id, at, actor_id, action, target_type, target_id, reason, details, ip_range } = e2;
  return `${prev_hash}
${canonicalJson({ id, at, actor_id, action, target_type, target_id, reason, details, ip_range })}`;
}
async function computeAuditHash(e2) {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(auditHashInput(e2))
  );
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}
async function verifyAuditChain(entries, startPrevHash = AUDIT_GENESIS_HASH) {
  let prev = startPrevHash;
  let checked = 0;
  for (const e2 of entries) {
    if (e2.prev_hash !== prev || await computeAuditHash(e2) !== e2.hash) {
      return { intact: false, checked, first_broken_id: e2.id };
    }
    prev = e2.hash;
    checked++;
  }
  return { intact: true, checked, first_broken_id: null };
}
var AdminErrorStatus = {
  INVALID_BODY: 400,
  REASON_REQUIRED: 400,
  /** No or unknown admin token. */
  UNAUTHENTICATED: 401,
  /** Idle 30 minutes or past 8 hours. */
  SESSION_EXPIRED: 401,
  INVALID_CREDENTIALS: 401,
  INVALID_MFA_CODE: 401,
  INVALID_TICKET: 401,
  /** The account has no staff role. Answered like INVALID_CREDENTIALS at sign-in. */
  NOT_STAFF: 403,
  /** A staff account without two-step can't sign in. */
  STAFF_MFA_REQUIRED: 403,
  /** `required_permission` names the key. */
  MISSING_PERMISSION: 403,
  /** A sensitive action without a fresh, valid `mfa_code`. */
  STEP_UP_REQUIRED: 403,
  /** The request comes from outside `console.ip_allowlist`. */
  IP_NOT_ALLOWED: 403,
  /** Needs a second staff member (Console v2 approvals). */
  APPROVAL_REQUIRED: 403,
  /** Acting on yourself, the Owner, or the last Owner. */
  PROTECTED_TARGET: 403,
  /** A read-only setting, or a flag that another endpoint controls. */
  READ_ONLY: 403,
  NOT_FOUND: 404,
  /** A stale `if_version`, a duplicate key or name, or a state that doesn't allow it. */
  CONFLICT: 409,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  /** Not built or not measured yet. */
  NOT_AVAILABLE: 501,
  // ---- Console v2 (2026-11-02) ----
  /** Reading messages needs a review session (`POST content/review` with a reason) first. */
  REVIEW_REQUIRED: 403,
  /** Nobody but the Owner approves their own request, and only by break-glass. */
  SELF_APPROVAL: 403,
  /** The official server can't be deleted, and its owner changes only through an approval. */
  OFFICIAL_GUILD: 409,
  /** A release's files are missing, the wrong ones, or their signature doesn't verify. */
  RELEASE_INVALID: 400
};
function isAdminApiError(value) {
  if (typeof value !== "object" || value === null) return false;
  const v = value;
  return typeof v.code === "string" && Object.hasOwn(AdminErrorStatus, v.code) && typeof v.message === "string";
}

// src/threads.ts
var THREAD_CHANNEL_TYPES = [
  ChannelType.ANNOUNCEMENT_THREAD,
  ChannelType.PUBLIC_THREAD,
  ChannelType.PRIVATE_THREAD
];
function isThreadChannelType(type) {
  return type !== void 0 && type !== null && THREAD_CHANNEL_TYPES.includes(type);
}
function canHaveThreads(type) {
  return type === ChannelType.GUILD_TEXT || type === ChannelType.GUILD_ANNOUNCEMENT;
}
var THREAD_AUTO_ARCHIVE_DURATIONS = [60, 1440, 4320, 10080];
var DEFAULT_THREAD_AUTO_ARCHIVE = 4320;
function isAutoArchiveDuration(v) {
  return THREAD_AUTO_ARCHIVE_DURATIONS.includes(v);
}
var THREAD_NAME_MAX = 100;
var MAX_ACTIVE_THREADS = 1e3;
var MAX_THREAD_MEMBERS = 1e3;
var THREAD_RATE_LIMIT_MAX = 21600;
var ARCHIVED_THREADS_PAGE = 50;
function threadNameFrom(content, fallback) {
  const flat = content.replace(/<a?:\w+:\d+>/g, "").replace(/<[@#][!&]?\d+>/g, "").replace(/[*_~`|>]/g, "").replace(/\s+/g, " ").trim();
  const cut = [...flat].slice(0, 40).join("");
  return cut || fallback;
}
function permissionThreadFromWire(thread) {
  return {
    type: thread.type,
    locked: !!thread.thread_metadata?.locked,
    archived: !!thread.thread_metadata?.archived,
    member: !!thread.member
  };
}
function threadArchivesAt(meta, lastMessageAt) {
  const since = Math.max(
    Date.parse(meta.archive_timestamp),
    lastMessageAt ? Date.parse(lastMessageAt) : 0
  );
  return since + meta.auto_archive_duration * 6e4;
}
var ThreadRoutes = {
  /** POST: start a public thread from a message. */
  messageThreads: (channelId, messageId) => `/channels/${channelId}/messages/${messageId}/threads`,
  /** POST: start a thread without a message (public or private). */
  channelThreads: (channelId) => `/channels/${channelId}/threads`,
  /** GET: archived public threads of a channel, newest archive first. */
  archivedPublic: (channelId) => `/channels/${channelId}/threads/archived/public`,
  /** GET: archived private threads of a channel (Manage Threads). */
  archivedPrivate: (channelId) => `/channels/${channelId}/threads/archived/private`,
  /** GET: archived private threads of a channel the caller joined. */
  joinedArchivedPrivate: (channelId) => `/channels/${channelId}/users/@me/threads/archived/private`,
  /** GET: every active thread of the server the caller may see, and their own memberships. */
  guildActive: (guildId) => `/guilds/${guildId}/threads/active`,
  /** GET: the thread's members. */
  members: (threadId) => `/channels/${threadId}/thread-members`,
  /** GET, PUT (join / add), DELETE (leave / remove). */
  member: (threadId, userId) => `/channels/${threadId}/thread-members/${userId}`
};

// src/polls.ts
var POLL_QUESTION_MAX = 300;
var POLL_ANSWER_MAX = 55;
var POLL_ANSWERS_MIN = 2;
var POLL_ANSWERS_MAX = 10;
var POLL_DURATION_MAX_HOURS = 768;
var POLL_DURATION_DEFAULT_HOURS = 24;
var POLL_DURATIONS_HOURS = [1, 4, 8, 24, 72, 168, 336];
var PollLayoutType = {
  DEFAULT: 1
};
var MessageReferenceType = {
  DEFAULT: 0,
  FORWARD: 1
};
var FORWARD_MAX_TARGETS = 5;

// src/emoji.ts
var EMOJI_NAME_MIN = 2;
var EMOJI_NAME_MAX = 32;
var EMOJI_NAME_PATTERN = /^[a-z0-9_]{2,32}$/;
var MAX_STATIC_EMOJIS = 50;
var MAX_ANIMATED_EMOJIS = 50;
var EMOJI_MAX_BYTES = IMAGE_LIMITS.emoji.maxBytes;
var EMOJI_SIZE = IMAGE_LIMITS.emoji.maxWidth;
var EMOJI_UPLOAD_TYPES = ["image/png", "image/gif", "image/webp"];
var EMOJI_NAME_TAKEN = "EMOJI_NAME_TAKEN";
var JUMBO_EMOJI_MAX = 27;
function isEmojiName(value) {
  return typeof value === "string" && EMOJI_NAME_PATTERN.test(value);
}
function emojiNameFrom(raw) {
  const base = raw.replace(/\.[a-z0-9]{1,5}$/i, "");
  let n = base.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "").slice(0, EMOJI_NAME_MAX);
  if (n.length < EMOJI_NAME_MIN) n = n ? `${n}_emoji`.slice(0, EMOJI_NAME_MAX) : "emoji";
  return n;
}
var CUSTOM_EMOJI_SOURCE = "<(a?):([A-Za-z0-9_]{1,32}):(\\d{1,20})>";
function parseCustomEmoji(s) {
  const m = new RegExp(`^${CUSTOM_EMOJI_SOURCE}$`).exec(s);
  return m ? { animated: m[1] === "a", name: m[2], id: m[3] } : null;
}
function formatCustomEmoji(e2) {
  return `<${e2.animated ? "a" : ""}:${e2.name}:${e2.id}>`;
}
function parseReactionEmoji(s) {
  const m = /^(?:a:)?([A-Za-z0-9_]{1,32}):(\d{1,20})$/.exec(s);
  return m ? { name: m[1], id: m[2] } : null;
}
function reactionRouteEmoji(e2) {
  return e2.id ? `${e2.name || "_"}:${e2.id}` : e2.name ?? "";
}
function emojiOfToken(token) {
  const c = parseCustomEmoji(token);
  return c ? { id: c.id, name: c.name, animated: c.animated } : { id: null, name: token };
}
function tokenOfEmoji(e2) {
  return e2.id ? formatCustomEmoji({ id: e2.id, name: e2.name || "_", animated: !!e2.animated }) : e2.name ?? "";
}
function reactionRouteOfToken(token) {
  return reactionRouteEmoji(emojiOfToken(token));
}
function sameReactionEmoji(a, b) {
  return a.id ? a.id === b.id : !b.id && a.name === b.name;
}
function customEmojiPath(id, animated = false) {
  return `/api/v1/emojis/${id}.${animated ? "gif" : "png"}`;
}
var EmojiRoutes = {
  guildEmojis: (guildId) => `/guilds/${guildId}/emojis`,
  guildEmoji: (guildId, emojiId) => `/guilds/${guildId}/emojis/${emojiId}`
};

// src/notifications.ts
var NotificationLevel = {
  ALL_MESSAGES: 0,
  ONLY_MENTIONS: 1,
  NO_MESSAGES: 2,
  INHERIT: 3
};
var MUTE_DURATIONS = [900, 3600, 10800, 28800, 86400, -1];
function isMuteDuration(v) {
  return typeof v === "number" && MUTE_DURATIONS.includes(v);
}
var NotifyRoutes = {
  guildSettings: (guildId) => `/users/@me/guilds/${guildId}/settings`
};
var NOTIFY_OVERRIDES_MAX = 500;
function defaultGuildSettings(guildId) {
  return {
    guild_id: guildId,
    message_notifications: NotificationLevel.INHERIT,
    suppress_everyone: false,
    suppress_roles: false,
    muted: false,
    mute_config: null,
    channel_overrides: []
  };
}
function muteActive(muted, cfg, now) {
  if (!muted) return false;
  if (!cfg?.end_time) return true;
  const end = Date.parse(cfg.end_time);
  return Number.isNaN(end) || end > now;
}
function overrideIsDefault(o, now) {
  return o.message_notifications === NotificationLevel.INHERIT && !muteActive(o.muted, o.mute_config, now);
}
function guildSettingsAreDefault(s, now) {
  return s.message_notifications === NotificationLevel.INHERIT && !s.suppress_everyone && !s.suppress_roles && !muteActive(s.muted, s.mute_config, now) && s.channel_overrides.every((o) => overrideIsDefault(o, now));
}
function overrideOf(s, channelId) {
  if (!s || !channelId) return void 0;
  return s.channel_overrides.find((o) => o.channel_id === channelId);
}
function guildLevel(s, guildDefault) {
  const own = s?.message_notifications;
  if (own === 0 || own === 1 || own === 2) return own;
  return guildDefault === NotificationLevel.ALL_MESSAGES ? 0 : 1;
}
function inheritedLevel(s, guildDefault, c) {
  const cat2 = overrideOf(s, c.parent_id)?.message_notifications;
  if (cat2 === 0 || cat2 === 1 || cat2 === 2) return cat2;
  return guildLevel(s, guildDefault);
}
function effectiveLevel(s, guildDefault, c) {
  const own = overrideOf(s, c.id)?.message_notifications;
  if (own === 0 || own === 1 || own === 2) return own;
  return inheritedLevel(s, guildDefault, c);
}
function guildMuted(s, now) {
  return !!s && muteActive(s.muted, s.mute_config, now);
}
function channelMuted(s, c, now) {
  if (!s) return false;
  if (guildMuted(s, now)) return true;
  const own = overrideOf(s, c.id);
  if (own && muteActive(own.muted, own.mute_config, now)) return true;
  const cat2 = overrideOf(s, c.parent_id);
  return !!cat2 && muteActive(cat2.muted, cat2.mute_config, now);
}
var kindsOf = (k) => !k ? [] : typeof k === "string" ? [k] : k;
function mentionCounts(s, guildDefault, c, kind) {
  if (effectiveLevel(s, guildDefault, c) === NotificationLevel.NO_MESSAGES) return false;
  return kindsOf(kind).some(
    (k) => k === "user" ? true : k === "everyone" ? !s?.suppress_everyone : !s?.suppress_roles
  );
}
function messageNotifies(s, guildDefault, c, kind, now) {
  if (kindsOf(kind).length && mentionCounts(s, guildDefault, c, kind)) return true;
  return effectiveLevel(s, guildDefault, c) === NotificationLevel.ALL_MESSAGES && !channelMuted(s, c, now);
}
function muteConfigAt(req, now) {
  const w = req.selected_time_window;
  return {
    end_time: w === -1 ? null : new Date(now + w * 1e3).toISOString(),
    selected_time_window: w
  };
}
function patchMute(cur, muted, cfg, now) {
  if (muted === false) return { muted: false, mute_config: null };
  if (cfg) return { muted: true, mute_config: muteConfigAt(cfg, now) };
  if (muted === true) {
    return cur.muted ? { muted: true, mute_config: cur.mute_config } : { muted: true, mute_config: { end_time: null, selected_time_window: -1 } };
  }
  return { muted: cur.muted, mute_config: cur.mute_config };
}
function applyGuildSettingsPatch(cur, p, now) {
  const next = {
    ...cur,
    ...p.message_notifications !== void 0 ? { message_notifications: p.message_notifications } : {},
    ...p.suppress_everyone !== void 0 ? { suppress_everyone: p.suppress_everyone } : {},
    ...p.suppress_roles !== void 0 ? { suppress_roles: p.suppress_roles } : {},
    ...patchMute(cur, p.muted, p.mute_config, now)
  };
  const byId = new Map(cur.channel_overrides.map((o) => [o.channel_id, o]));
  for (const [cid, ch] of Object.entries(p.channel_overrides ?? {})) {
    if (ch === null) {
      byId.delete(cid);
      continue;
    }
    const prev = byId.get(cid) ?? {
      channel_id: cid,
      message_notifications: NotificationLevel.INHERIT,
      muted: false,
      mute_config: null
    };
    const o = {
      ...prev,
      ...ch.message_notifications !== void 0 ? { message_notifications: ch.message_notifications } : {},
      ...patchMute(prev, ch.muted, ch.mute_config, now)
    };
    if (overrideIsDefault(o, now)) byId.delete(cid);
    else byId.set(cid, o);
  }
  next.channel_overrides = [...byId.values()];
  return next;
}
function nextMuteEnd(s, now) {
  if (!s) return null;
  let next = null;
  const see = (muted, cfg) => {
    if (!muted || !cfg?.end_time) return;
    const end = Date.parse(cfg.end_time);
    if (!Number.isNaN(end) && end > now && (next === null || end < next)) next = end;
  };
  see(s.muted, s.mute_config);
  for (const o of s.channel_overrides) see(o.muted, o.mute_config);
  return next;
}

// src/automod.ts
var AutoModTriggerType = {
  KEYWORD: 1,
  SPAM: 3,
  KEYWORD_PRESET: 4,
  MENTION_SPAM: 5,
  MEMBER_PROFILE: 6
};
var AUTOMOD_BUILT_TRIGGERS = [
  AutoModTriggerType.KEYWORD,
  AutoModTriggerType.MENTION_SPAM,
  AutoModTriggerType.SPAM
];
var AutoModEventType = {
  MESSAGE_SEND: 1,
  MEMBER_UPDATE: 2
};
var AutoModActionType = {
  BLOCK_MESSAGE: 1,
  SEND_ALERT_MESSAGE: 2,
  TIMEOUT: 3,
  BLOCK_MEMBER_INTERACTION: 4
};
var AUTOMOD_LIMITS = Object.freeze({
  NAME_MAX: 100,
  /** Rules of each trigger type a server may have. */
  RULES_PER_TYPE: {
    [AutoModTriggerType.KEYWORD]: 6,
    [AutoModTriggerType.MENTION_SPAM]: 1,
    [AutoModTriggerType.SPAM]: 1
  },
  KEYWORDS_MAX: 1e3,
  KEYWORD_LENGTH_MAX: 60,
  ALLOW_LIST_MAX: 100,
  MENTION_LIMIT_MIN: 1,
  MENTION_LIMIT_MAX: 50,
  MENTION_LIMIT_DEFAULT: 20,
  EXEMPT_ROLES_MAX: 20,
  EXEMPT_CHANNELS_MAX: 50,
  CUSTOM_MESSAGE_MAX: 150,
  TIMEOUT_MIN_S: 60,
  TIMEOUT_MAX_S: 604800
});
var AUTOMOD_TIMEOUT_CHOICES = [60, 300, 600, 3600, 86400, 604800];
var AUTOMOD_SPAM = Object.freeze({ REPEATS: 3, WINDOW_S: 60 });
function automodActionAllowed(trigger, action) {
  if (action === AutoModActionType.BLOCK_MESSAGE) return true;
  if (action === AutoModActionType.SEND_ALERT_MESSAGE) return true;
  if (action === AutoModActionType.TIMEOUT)
    return trigger === AutoModTriggerType.KEYWORD || trigger === AutoModTriggerType.MENTION_SPAM;
  return false;
}
var AutoModRoutes = {
  rules: (guildId) => `/guilds/${guildId}/auto-moderation/rules`,
  rule: (guildId, ruleId) => `/guilds/${guildId}/auto-moderation/rules/${ruleId}`
};
function isAutoModAlertEmbed(v) {
  return typeof v === "object" && v !== null && v.type === "auto_moderation_message" && Array.isArray(v.fields);
}
function alertField(e2, name) {
  return e2.fields.find((f) => f.name === name)?.value ?? null;
}
var WORD = "[\\p{L}\\p{N}\\p{M}_]";
var START = `(?<!${WORD})`;
var END = `(?!${WORD})`;
var SPECIAL = /[.*+?^${}()|[\]\\]/g;
function keywordProblem(keyword) {
  const k = keyword.trim();
  if ([...k].length > AUTOMOD_LIMITS.KEYWORD_LENGTH_MAX) return "too_long";
  if (!normalizeForSearch(k.replace(/^\*+|\*+$/g, ""))) return "empty";
  return null;
}
function parse(keyword) {
  const k = keyword.trim();
  const open = k.startsWith("*");
  const tail = k.length > 1 && k.endsWith("*");
  const core = normalizeForSearch(k.replace(/^\*+|\*+$/g, ""));
  return core ? { core, open, tail } : null;
}
function groups(list) {
  const by = /* @__PURE__ */ new Map();
  for (const k of list) {
    const p = parse(k);
    if (!p) continue;
    const key = `${p.open ? 1 : 0}${p.tail ? 1 : 0}`;
    const g2 = by.get(key) ?? { cores: /* @__PURE__ */ new Map() };
    if (!g2.cores.has(p.core)) g2.cores.set(p.core, k.trim());
    by.set(key, g2);
  }
  const out = [];
  for (const [key, g2] of by) {
    const alts = [...g2.cores.keys()].sort((a, b) => b.length - a.length).map((c) => c.replace(SPECIAL, "\\$&"));
    const re = new RegExp(
      `${key[0] === "1" ? "" : START}(?:${alts.join("|")})${key[1] === "1" ? "" : END}`,
      "gu"
    );
    out.push({ re, byCore: g2.cores });
  }
  return out;
}
var WORD_CHAR = new RegExp(WORD, "u");
function widen(text2, start, end) {
  let s = start;
  let e2 = end;
  while (s > 0 && WORD_CHAR.test(text2[s - 1])) s--;
  while (e2 < text2.length && WORD_CHAR.test(text2[e2])) e2++;
  return [s, e2];
}
function compileKeywordMatcher(keywords, allow = []) {
  const blocked = groups(keywords);
  const allowed = groups(allow);
  return {
    test(text2) {
      if (!text2 || blocked.length === 0) return null;
      let spans = null;
      const allowedSpans = () => {
        if (spans) return spans;
        spans = [];
        for (const g2 of allowed) {
          g2.re.lastIndex = 0;
          for (const m of text2.matchAll(g2.re)) spans.push([m.index, m.index + m[0].length]);
        }
        return spans;
      };
      let best = null;
      for (const g2 of blocked) {
        g2.re.lastIndex = 0;
        for (const m of text2.matchAll(g2.re)) {
          const s = m.index;
          const e2 = s + m[0].length;
          if (allowedSpans().some(([a, b]) => a <= s && e2 <= b)) continue;
          if (!best || s < best.at) {
            const [ws, we] = widen(text2, s, e2);
            best = {
              at: s,
              hit: { keyword: g2.byCore.get(m[0]) ?? m[0], matched: text2.slice(ws, we) }
            };
          }
          break;
        }
      }
      return best?.hit ?? null;
    }
  };
}
var EDGE = /^[^\p{L}\p{N}\p{M}_]+|[^\p{L}\p{N}\p{M}_]+$/gu;
function matchedAsWritten(content, matched) {
  if (!matched) return matched;
  const words = content.split(/\s+/u).filter(Boolean);
  const n = Math.max(1, matched.split(" ").length);
  let loose = null;
  for (let i = 0; i + n <= words.length; i++) {
    const span = words.slice(i, i + n).join(" ");
    const norm = normalizeForSearch(span);
    if (norm === matched) return span;
    if (loose === null && norm.includes(matched)) loose = span.replace(EDGE, "");
  }
  return loose || matched;
}
function matchKeywords(content, keywords, allow = []) {
  return compileKeywordMatcher(keywords, allow).test(normalizeForSearch(content));
}
function uniqueMentionCount(content) {
  const seen = /* @__PURE__ */ new Set();
  for (const m of content.matchAll(/<@(!|&)?(\d{1,20})>/g)) {
    seen.add((m[1] === "&" ? "r" : "u") + m[2]);
  }
  return seen.size;
}
var SLOWMODE_CHOICES = [
  0,
  5,
  10,
  15,
  30,
  60,
  120,
  300,
  600,
  900,
  1800,
  3600,
  7200,
  21600
];

// src/events.ts
var GuildScheduledEventEntityType = {
  STAGE_INSTANCE: 1,
  VOICE: 2,
  EXTERNAL: 3
};
var GuildScheduledEventStatus = {
  SCHEDULED: 1,
  ACTIVE: 2,
  COMPLETED: 3,
  CANCELED: 4
};
var GuildScheduledEventPrivacyLevel = { GUILD_ONLY: 2 };
var SCHEDULED_EVENT_LIMITS = Object.freeze({
  NAME_MAX: 100,
  DESCRIPTION_MAX: 1e3,
  LOCATION_MAX: 100,
  /** Events not yet finished (SCHEDULED or ACTIVE) per server: Discord's 100. */
  MAX_UNFINISHED: 100,
  /** How far ahead an event may start: 5 years (a typo's year 20026 is refused). */
  MAX_AHEAD_MS: 5 * 365 * 864e5,
  /** Interested people per page (Discord's `limit` maximum). */
  USERS_PAGE_MAX: 100,
  /**
   * A started voice or stage event ends once its channel has been empty
   * this long (Discord ends it "after a few minutes").
   */
  EMPTY_END_MS: 5 * 6e4,
  /** "Interested" toggles per person per minute (each one reaches every member). */
  INTEREST_PER_MINUTE: 20
});
var ScheduledEventErrorCode = {
  UNKNOWN_GUILD_SCHEDULED_EVENT: 10070,
  UNKNOWN_GUILD_SCHEDULED_EVENT_USER: 10071,
  /** "Maximum number of uncompleted guild scheduled events reached (100)". */
  MAX_GUILD_SCHEDULED_EVENTS: 30038,
  /** "Cannot update a finished event". */
  CANNOT_UPDATE_FINISHED_EVENT: 18e4
};
var ScheduledEventRoutes = {
  guildScheduledEvents: (guildId) => `/guilds/${guildId}/scheduled-events`,
  guildScheduledEvent: (guildId, eventId) => `/guilds/${guildId}/scheduled-events/${eventId}`,
  guildScheduledEventUsers: (guildId, eventId) => `/guilds/${guildId}/scheduled-events/${eventId}/users`,
  /** `PUT` marks you interested, `DELETE` takes it back (Discord's client route). */
  guildScheduledEventMe: (guildId, eventId) => `/guilds/${guildId}/scheduled-events/${eventId}/users/@me`,
  /** The cover image (public, content-addressed). */
  eventCover: (eventId, file) => `/event-covers/${eventId}/${file}`
};
function scheduledEventTransitionAllowed(from, to) {
  if (from === to) return true;
  if (from === GuildScheduledEventStatus.SCHEDULED)
    return to === GuildScheduledEventStatus.ACTIVE || to === GuildScheduledEventStatus.CANCELED;
  if (from === GuildScheduledEventStatus.ACTIVE) return to === GuildScheduledEventStatus.COMPLETED;
  return false;
}
function isUnfinishedEvent(e2) {
  return e2.status === GuildScheduledEventStatus.SCHEDULED || e2.status === GuildScheduledEventStatus.ACTIVE;
}
function isChannelEvent(e2) {
  return e2.entity_type !== GuildScheduledEventEntityType.EXTERNAL;
}
function compareScheduledEvents(a, b) {
  const live = (e2) => e2.status === GuildScheduledEventStatus.ACTIVE ? 0 : 1;
  if (live(a) !== live(b)) return live(a) - live(b);
  const t = Date.parse(a.scheduled_start_time) - Date.parse(b.scheduled_start_time);
  if (t !== 0) return t;
  return a.id.length !== b.id.length ? a.id.length - b.id.length : a.id < b.id ? -1 : 1;
}

// src/social.ts
var SocialRoutes = {
  /** `GET`: every note of yours, `{ [user_id]: note }` (Discord's shape). */
  meNotes: () => "/users/@me/notes",
  /** `GET` → `UserNote`; `PUT` `PutUserNoteRequest` → 204. */
  meNote: (userId) => `/users/@me/notes/${userId}`,
  /** `POST` `AccountRemovalRequest` → 204: the 14-day deletion hold starts. */
  meDelete: () => "/users/@me/delete",
  /** `POST` `AccountRemovalRequest` → 204: the account is disabled until you sign in. */
  meDisable: () => "/users/@me/disable"
};
var USER_NOTE_MAX_LENGTH = 256;
var USER_NOTES_MAX = 2e3;
function normalizeNote(raw) {
  if (raw === null || raw === void 0) return null;
  if (typeof raw !== "string") return false;
  const v = raw.trim();
  if (!v) return null;
  return [...v].length > USER_NOTE_MAX_LENGTH ? false : v;
}
var ACCOUNT_DELETION_HOLD_DAYS = 14;
var ACTIVITY_NAME_MAX_LENGTH = 128;
var START_MAX_AGE_MS = 7 * 864e5;
var START_SKEW_MS = 6e4;
function playingActivity(name, start) {
  return { name, type: ActivityType.PLAYING, created_at: start, timestamps: { start } };
}
function readGameActivity(raw, now = Date.now()) {
  if (!Array.isArray(raw)) return null;
  for (const a of raw.slice(0, 5)) {
    if (typeof a !== "object" || a === null) continue;
    const r = a;
    if (r.type !== ActivityType.PLAYING || typeof r.name !== "string") continue;
    const name = r.name.replace(/\s+/g, " ").trim();
    if (!name) continue;
    const cut = [...name].slice(0, ACTIVITY_NAME_MAX_LENGTH).join("");
    const ts = r.timestamps;
    const given = typeof ts?.start === "number" ? ts.start : r.created_at;
    let start = typeof given === "number" && Number.isFinite(given) ? Math.floor(given) : now;
    if (start > now + START_SKEW_MS || start < now - START_MAX_AGE_MS) start = now;
    return { name: cut, start: Math.min(start, now) };
  }
  return null;
}
function playingOf(p) {
  return p?.activities?.find((a) => a.type === ActivityType.PLAYING && !!a.name) ?? null;
}
function activityStart(a) {
  const s = a?.timestamps?.start ?? a?.created_at;
  return typeof s === "number" && Number.isFinite(s) ? s : null;
}
function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1e3));
  const h = Math.floor(total / 3600);
  const m = Math.floor(total % 3600 / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

// src/calls.ts
var CALL_RING_TIMEOUT_MS = 3e4;
var CALL_REGION = "default";
var CallRoutes = {
  call: (channelId) => `/channels/${channelId}/call`,
  ring: (channelId) => `/channels/${channelId}/call/ring`,
  stopRinging: (channelId) => `/channels/${channelId}/call/stop-ringing`
};

// src/console-v2/routes.ts
var ae2 = encodeURIComponent;
var g = (guildId) => `/community/guilds/${guildId}`;
var AdminRoutesV2 = {
  // ---- 3.2 community (`community.manage`) -------------------------------
  /** GET, PUT `{mode, reason}`: who may create a server. */
  communityCreation: () => "/community/creation",
  /** GET `?q&kind=official|user|deleted&owner_id&cursor`. */
  communityGuilds: () => "/community/guilds",
  /** GET, PATCH; DELETE `{reason, mfa_code}` (never the official server). */
  communityGuild: (guildId) => g(guildId),
  /** POST: restore a deleted server (within 7 days). */
  communityGuildRestore: (guildId) => `${g(guildId)}/restore`,
  /** GET (with deleted ones), POST create, PATCH positions. */
  communityChannels: (guildId) => `${g(guildId)}/channels`,
  /** PATCH, DELETE. */
  communityChannel: (guildId, channelId) => `${g(guildId)}/channels/${channelId}`,
  communityChannelRestore: (guildId, channelId) => `${g(guildId)}/channels/${channelId}/restore`,
  /** GET (with deleted ones), POST create, PATCH positions. */
  communityRoles: (guildId) => `${g(guildId)}/roles`,
  /** PATCH, DELETE. */
  communityRole: (guildId, roleId) => `${g(guildId)}/roles/${roleId}`,
  communityRoleRestore: (guildId, roleId) => `${g(guildId)}/roles/${roleId}/restore`,
  /** GET `?cursor`: who holds the role. */
  communityRoleMembers: (guildId, roleId) => `${g(guildId)}/roles/${roleId}/members`,
  /** GET `?q&role_id&cursor`. */
  communityMembers: (guildId) => `${g(guildId)}/members`,
  /** POST `{user_ids≤200, add[], remove[], reason}`. */
  communityMembersRoles: (guildId) => `${g(guildId)}/members/roles`,
  /** DELETE `{reason}`: kick (never the owner). */
  communityMember: (guildId, userId) => `${g(guildId)}/members/${userId}`,
  /** PUT `{until|null, reason}`. */
  communityMemberTimeout: (guildId, userId) => `${g(guildId)}/members/${userId}/timeout`,
  /** GET. */
  communityBans: (guildId) => `${g(guildId)}/bans`,
  /** PUT, DELETE. */
  communityBan: (guildId, userId) => `${g(guildId)}/bans/${userId}`,
  /** GET, POST. */
  communityInvites: (guildId) => `${g(guildId)}/invites`,
  /** DELETE. */
  communityInvite: (guildId, code) => `${g(guildId)}/invites/${ae2(code)}`,
  /** GET: the server's own audit log. */
  communityAuditLog: (guildId) => `${g(guildId)}/audit-log`,
  /** PUT `{user_id, reason, mfa_code}`: 200, or 202 `{approval}` for the official server. */
  communityOwner: (guildId) => `${g(guildId)}/owner`,
  // ---- 3.3 moderation -----------------------------------------------------
  /** GET `?tier&category&state&assignee&source&cursor` (`moderation.reports`, Pollable 15 s). */
  moderationQueue: () => "/moderation/queue",
  /** POST: open a case by hand. */
  moderationCases: () => "/moderation/cases",
  /** GET, PATCH. */
  moderationCase: (caseId) => `/moderation/cases/${caseId}`,
  moderationCaseClaim: (caseId) => `/moderation/cases/${caseId}/claim`,
  moderationCaseNotes: (caseId) => `/moderation/cases/${caseId}/notes`,
  moderationCaseLinks: (caseId) => `/moderation/cases/${caseId}/links`,
  moderationCaseClose: (caseId) => `/moderation/cases/${caseId}/close`,
  /** POST `{action, reason, mfa_code?}` (`moderation.actions`). */
  moderationCaseActions: (caseId) => `/moderation/cases/${caseId}/actions`,
  /** POST `{reason}`: decrypt one evidence snapshot (audited). */
  moderationEvidenceReveal: (evidenceId) => `/moderation/evidence/${evidenceId}/reveal`,
  /** GET (`moderation.appeals`). */
  moderationAppeals: () => "/moderation/appeals",
  moderationAppeal: (appealId) => `/moderation/appeals/${appealId}`,
  /** POST `{outcome, note, reason}`. */
  moderationAppealDecide: (appealId) => `/moderation/appeals/${appealId}/decide`,
  /** GET, POST (`moderation.filters`). */
  moderationFilters: () => "/moderation/filters",
  /** PATCH, DELETE. */
  moderationFilter: (filterId) => `/moderation/filters/${filterId}`,
  /** POST `{text}`: what the filters would highlight. */
  moderationFiltersTest: () => "/moderation/filters/test",
  /** GET `?state&cursor`. */
  moderationFlags: () => "/moderation/flags",
  /** POST `{state dismissed|cased}`. */
  moderationFlag: (flagId) => `/moderation/flags/${flagId}`,
  /** GET, PUT `{active, minutes, reason}` (3.4, `moderation.actions`, no two-step). */
  moderationRaid: () => "/moderation/raid",
  // ---- 3.5 messages and media (`content.read`) ----------------------------
  /** POST `{reason, include_dms}`: a review session. */
  contentReview: () => "/content/review",
  /** DELETE: end it. */
  contentReviewEnd: (reviewId) => `/content/review/${ae2(reviewId)}`,
  /** GET `?review_id&q&author_id&channel_id&guild_id&scope&from&to&has&flagged&before_id&limit`. */
  contentMessages: () => "/content/messages",
  /** GET (by ID or pasted link, `?review_id`); DELETE `{reason}` (`content.delete`). */
  contentMessage: (messageId) => `/content/messages/${messageId}`,
  /** GET `?review_id&around|before_id|after_id`: 25 on each side. */
  contentChannelMessages: (channelId) => `/content/channels/${channelId}/messages`,
  /** GET `?review_id&user_id&type` (`content.read_dms`). */
  contentConversations: () => "/content/conversations",
  /** GET `?review_id&uploader_id&channel_id&scope&type&from&to&before_id&limit`. */
  contentAttachments: () => "/content/attachments",
  /** DELETE `{reason}` (`content.delete`). */
  contentAttachment: (attachmentId) => `/content/attachments/${attachmentId}`,
  /** GET `?exp&sig`: a signed thumbnail link (5 minutes), for `<img loading=lazy>`. */
  contentThumb: (attachmentId) => `/content/thumbs/${attachmentId}`,
  /** GET. */
  contentStorage: () => "/content/storage",
  /** POST `{user_id, from, to, scope, reason, mfa_code, dry_run}` (`content.delete`). */
  contentPurge: () => "/content/purge",
  // ---- 3.6 voice (`voice.manage`) -----------------------------------------
  /** GET (Pollable, 3 s while the page is visible). */
  voiceRooms: () => "/voice/rooms",
  /** GET. */
  voiceEngine: () => "/voice/engine",
  /** POST `AdminVoiceActionRequest`. */
  voiceActions: () => "/voice/actions",
  // ---- 3.7 releases (`releases.manage`) -----------------------------------
  /** GET (rows, and versions found on disk as drafts); POST `{version, notes, channel}`. */
  releases: () => "/releases",
  /** GET, PUT `{min_version, reason}`. Matched before `release(v)`. */
  releasesMinVersion: () => "/releases/min-version",
  /** PATCH `{notes?, channel?}`. */
  release: (version) => `/releases/${ae2(version)}`,
  /** PUT octet-stream (at most 300 MB): the setup exe, the NSIS exe or the `.sig`. */
  releaseFile: (version, name) => `/releases/${ae2(version)}/files/${ae2(name)}`,
  /** POST `{rollout_percent, reason, mfa_code}`. */
  releasePublish: (version) => `/releases/${ae2(version)}/publish`,
  /** POST `{reason}`. */
  releaseWithdraw: (version) => `/releases/${ae2(version)}/withdraw`,
  // ---- 3.8 analytics (`analytics.read`) -----------------------------------
  /** GET `?metrics&bucket&from&to` (at most 2000 points; null for gaps). */
  analyticsSeries: () => "/analytics/series",
  /** GET: DAU, WAU, MAU. */
  analyticsActive: () => "/analytics/active",
  /** GET `?weeks≤26`. */
  analyticsRetention: () => "/analytics/retention",
  /** GET. */
  analyticsQuality: () => "/analytics/quality",
  /** GET `?metrics&bucket&from&to`: CSV (audited `analytics.export`). */
  analyticsExport: () => "/analytics/export.csv",
  // ---- 3.9 approvals, staff and the remaining placeholders ----------------
  /** GET `?state`. */
  approvals: () => "/approvals",
  /** GET; DELETE: the requester cancels. */
  approval: (approvalId) => `/approvals/${approvalId}`,
  /** POST `{mfa_code}`. */
  approvalApprove: (approvalId) => `/approvals/${approvalId}/approve`,
  /** POST `{reason}`. */
  approvalReject: (approvalId) => `/approvals/${approvalId}/reject`,
  /** PUT `{grants, reason, mfa_code}` (Owner only). */
  staffGrants: (userId) => `/staff/${userId}/grants`,
  /** GET `?q&cursor`: verified usernames (`usernames.manage`). */
  usernamesVerified: () => "/usernames/verified",
  /** PUT `{verified, reason}`. */
  usernameVerified: (userId) => `/usernames/verified/${userId}`,
  /** POST `{reason, mfa_code}`: `server` (graceful exit) or `comms-voice`. */
  systemServiceRestart: (name) => `/system/services/${ae2(name)}/restart`,
  /** GET: CSV of the audit log, filtered as `/audit` (audited `audit.export`). */
  auditExport: () => "/audit/export.csv",
  /** GET: a signed 24-hour link to the user's export (audited `user.export.download`). */
  userExportLink: (userId) => `/users/${userId}/export`
};

// src/console-v2/community.ts
var GUILD_NAME_MIN = 2;
var GUILD_NAME_MAX = 100;
var GUILD_PURGE_AFTER_MS = 7 * 864e5;
var GUILD_CREATE_RATE = { count: 5, window_s: 3600 };
var GUILD_CREATED_JOIN_SOURCE = "created";
var ADMIN_BULK_ROLES_MAX = 200;

// src/console-v2/moderation.ts
var CASE_SLA_MS = Object.freeze({
  0: 36e5,
  1: 24 * 36e5,
  2: 72 * 36e5,
  3: 7 * 24 * 36e5
});
var cat = (id, tier, subcategories = [], extra = {}) => ({ id, tier, subcategories, ...extra });
var REPORT_CATEGORIES = Object.freeze([
  cat("child_safety", 0, [
    { id: "sexual_content_minor", tier: 0 },
    { id: "grooming", tier: 0 },
    { id: "other_child_danger", tier: 0 }
  ]),
  cat("threat_violence", 0, [
    { id: "threat_to_life", tier: 0 },
    { id: "encouraging_violence", tier: 1 },
    { id: "graphic_violence", tier: 2 }
  ]),
  cat("self_harm", 0, [
    { id: "in_danger_now", tier: 0 },
    { id: "encourages_self_harm", tier: 1 }
  ]),
  cat("private_info", 1),
  cat("intimate_images", 1),
  cat("extortion", 1),
  cat("violent_extremism", 1),
  cat("harassment", 2),
  cat("hate", 2),
  cat("sexual_content", 2),
  cat("scam", 2),
  cat("impersonation", 2),
  cat("dangerous_goods", 2),
  cat("hacked_account", 2),
  cat("under_18", 2),
  cat("spam", 3),
  cat("copyright", 3),
  cat("server_rules", 3, [], { guildOnly: true }),
  cat("other", 3)
]);
function reportCategory(id) {
  return REPORT_CATEGORIES.find((c) => c.id === id) ?? null;
}
function reportTier(category, subcategory) {
  const c = reportCategory(category);
  if (!c) return null;
  if (c.subcategories.length === 0) return subcategory == null ? c.tier : null;
  if (subcategory == null) return c.tier;
  return c.subcategories.find((s) => s.id === subcategory)?.tier ?? null;
}
var WARNING_REPORT_CATEGORY = Object.freeze({
  spam: "spam",
  harassment: "harassment",
  hate: "hate",
  violence: "threat_violence",
  sexual_content: "sexual_content",
  impersonation: "impersonation",
  scam: "scam",
  illegal: "dangerous_goods",
  other: "other"
});
var REPORT_NOTE_MAX = 1e3;
var REPORT_RATE = { count: 10, window_s: 3600 };
var REPORT_DEDUPE_MS = 24 * 36e5;
var EVIDENCE_CONTEXT = 10;
var CASE_RETENTION_MS = 365 * 864e5;
var APPEAL_STATEMENT_MAX = 1e3;
var APPEAL_WINDOW_MS = 180 * 864e5;
var WORD_FILTERS_MAX = 1e3;
var WORD_FILTER_TERM_MAX = 100;

// src/console-v2/content.ts
var REVIEW_IDLE_MS = 30 * 6e4;
var CONTENT_SEARCH_LIMIT_MAX = 100;
var CONTENT_Q_MIN = 3;
var CONVERSATION_SIDE = 25;
var CONTENT_ATTACHMENTS_LIMIT_MAX = 60;
var THUMB_LINK_TTL_MS = 5 * 6e4;
var THUMB_MAX_SIDE = 320;
var PURGE_MAX = 5e4;
var PURGE_CHUNK = 500;

// src/console-v2/voice.ts
var COMMS_VOICE_ROOMS_PATH = "/internal/rooms";
var COMMS_VOICE_STOP_PUBLICATION_PATH = "/internal/voice/stop-publication";
var COMMS_VOICE_SHUTDOWN_PATH = "/internal/shutdown";

// src/console-v2/releases.ts
var RELEASE_CHANNELS = ["stable", "beta"];
var RELEASE_UPLOAD_MAX_BYTES = 300 * 1024 * 1024;
var RELEASE_VERSION_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;
function releaseFileKind(name, version) {
  if (name === `Neosocial-Setup-${version}.exe`) return "setup";
  if (name === `Neosocial-Setup-${version}.exe.sig`) return "setup_signature";
  if (name === `Neosocial_${version}_x64-setup.exe`) return "nsis";
  if (name === `Neosocial_${version}_x64-setup.exe.sig`) return "signature";
  return null;
}
var DESKTOP_INSTALL_ID_HEADER = "X-Install-Id";

// src/console-v2/analytics.ts
var ANALYTICS_MAX_POINTS = 2e3;
var ANALYTICS_RETENTION_WEEKS_MAX = 26;
var ACTIVE_DAYS_KEEP = 400;
var ANALYTICS_METRICS = [
  "online_users",
  "voice_users",
  "messages",
  "signups",
  "voice_minutes",
  "online_desktop",
  "online_web",
  "online_mobile",
  "active_channels",
  "reconnects",
  "signups_started",
  "visits",
  "voice_loss_p50",
  "voice_loss_p95",
  "voice_jitter_p50",
  "voice_jitter_p95"
];
var ANALYTICS_RANGES = Object.freeze({
  "24h": { ms: 864e5, bucket: "hour" },
  "7d": { ms: 7 * 864e5, bucket: "hour" },
  "30d": { ms: 30 * 864e5, bucket: "day" },
  "90d": { ms: 90 * 864e5, bucket: "day" }
});

// src/console-v2/approvals.ts
var APPROVAL_ACTIONS = [
  "user.delete_now",
  "user.suspend_permanent",
  "user.disable",
  "community.owner_reassign_official"
];
var APPROVAL_ACTION_PERMISSIONS = Object.freeze({
  "user.delete_now": "users.delete",
  "user.suspend_permanent": "users.suspend",
  "user.disable": "users.suspend",
  "community.owner_reassign_official": "community.manage"
});
var APPROVAL_TTL_MS = 72 * 36e5;
var APPROVAL_BREAK_GLASS_MS = 24 * 36e5;
var STAFF_GRANTABLE = ["content.read_dms"];
var RESTARTABLE_SERVICES = ["server", "comms-voice"];

// src/console-v2/raid.ts
var RAID_STATE_KEY = "raid.state";
var RAID_MINUTES_MIN = 15;
var RAID_MINUTES_MAX = 1440;
var RAID_NEW_ACCOUNT_MESSAGE_S = 10;
var DEFAULT_RAID_STATE = Object.freeze({
  active: false,
  started_at: null,
  ends_at: null,
  started_by: null,
  reason: null
});
function raidActive(state, nowMs) {
  return state.active && !!state.ends_at && Date.parse(state.ends_at) > nowMs;
}
export {
  ACCOUNT_DELETION_HOLD_DAYS,
  ACTIVE_DAYS_KEEP,
  ACTIVITY_NAME_MAX_LENGTH,
  ADMIN_API_BASE,
  ADMIN_BULK_ROLES_MAX,
  ADMIN_PAGE_DEFAULT,
  ADMIN_PAGE_MAX,
  ADMIN_REASON_MAX,
  ADMIN_REASON_MIN,
  ADMIN_SENSITIVE_USER_ACTIONS,
  ADMIN_SESSION_IDLE_MS,
  ADMIN_SESSION_MAX_MS,
  ADMIN_USER_ACTION_PERMISSIONS,
  AFK_TIMEOUTS,
  ALL_NX_PERMISSIONS,
  ALL_PERMISSIONS,
  ANALYTICS_MAX_POINTS,
  ANALYTICS_METRICS,
  ANALYTICS_RANGES,
  ANALYTICS_RETENTION_WEEKS_MAX,
  API_BASE,
  API_VERSION,
  APPEAL_STATEMENT_MAX,
  APPEAL_WINDOW_MS,
  APPROVAL_ACTIONS,
  APPROVAL_ACTION_PERMISSIONS,
  APPROVAL_BREAK_GLASS_MS,
  APPROVAL_TTL_MS,
  APP_SETTING_DEFS,
  APP_SETTING_KEYS,
  APP_VERSION_PATTERN,
  ARCHIVED_THREADS_PAGE,
  AUDIT_ACTION_GROUPS,
  AUDIT_GENESIS_HASH,
  AUDIT_LOG_REASON_HEADER,
  AUTOMOD_BUILT_TRIGGERS,
  AUTOMOD_LIMITS,
  AUTOMOD_SPAM,
  AUTOMOD_TIMEOUT_CHOICES,
  ActivityType,
  AdminErrorStatus,
  AdminRoutes,
  AdminRoutesV2,
  ApiErrorCode,
  AuditLogEvent,
  AutoModActionType,
  AutoModEventType,
  AutoModRoutes,
  AutoModTriggerType,
  BACKGROUND_SETTINGS,
  BANNER_ASPECT,
  BUILTIN_STAFF_ROLES,
  CALL_REGION,
  CALL_RING_TIMEOUT_MS,
  CASE_RETENTION_MS,
  CASE_SLA_MS,
  CLIENT_CONFIG_PATH,
  COMMS_ONLY_NX_PERMISSIONS,
  COMMS_VOICE_CHANNEL_BAN_PATH,
  COMMS_VOICE_DISCONNECT_PATH,
  COMMS_VOICE_GRANTS_PATH,
  COMMS_VOICE_MOVE_PATH,
  COMMS_VOICE_MUTE_PATH,
  COMMS_VOICE_REFRESH_PATH,
  COMMS_VOICE_ROOMS_PATH,
  COMMS_VOICE_SHUTDOWN_PATH,
  COMMS_VOICE_STATS_PATH,
  COMMS_VOICE_STOP_PUBLICATION_PATH,
  COMMS_VOICE_SYNC_PATH,
  CONNECT_DEPENDENT,
  CONTENT_ATTACHMENTS_LIMIT_MAX,
  CONTENT_Q_MIN,
  CONTENT_SEARCH_LIMIT_MAX,
  CONVERSATION_SIDE,
  CUSTOM_EMOJI_SOURCE,
  CallRoutes,
  ChannelType,
  DEFAULT_AFK_TIMEOUT,
  DEFAULT_EVERYONE_NX_PERMISSIONS,
  DEFAULT_EVERYONE_PERMISSIONS,
  DEFAULT_HEARTBEAT_INTERVAL_MS,
  DEFAULT_LIMIT_FEATURES,
  DEFAULT_MAINTENANCE,
  DEFAULT_NOISE_SUPPRESSION,
  DEFAULT_PRIVACY_SETTINGS,
  DEFAULT_RAID_STATE,
  DEFAULT_THREAD_AUTO_ARCHIVE,
  DESKTOP_BRIDGE_GLOBAL,
  DESKTOP_BRIDGE_VERSION,
  DESKTOP_GAME_EVENT,
  DESKTOP_INSTALL_ID_HEADER,
  DESKTOP_UPDATER_PUBKEY,
  DISK_ALERT_PERCENT,
  DISPLAY_NAME_MAX,
  DISPLAY_NAME_MAX_CODE_POINTS,
  DISPLAY_NAME_MIN,
  DesktopEvents,
  EMAIL_CODE_MAX_ATTEMPTS,
  EMAIL_CODE_TTL_MS,
  EMAIL_LOCK_AFTER_RECOVERY_MS,
  EMAIL_MAX,
  EMOJI_MAX_BYTES,
  EMOJI_NAME_MAX,
  EMOJI_NAME_MIN,
  EMOJI_NAME_PATTERN,
  EMOJI_NAME_TAKEN,
  EMOJI_SIZE,
  EMOJI_UPLOAD_TYPES,
  EVIDENCE_CONTEXT,
  EmojiRoutes,
  FEATURE_FLAGS_GLOBAL_ONLY,
  FEATURE_FLAGS_HIDDEN,
  FEATURE_FLAGS_NOT_BUILT,
  FEATURE_FLAG_DEFAULTS,
  FEATURE_FLAG_KEYS,
  FLAG_KEY_PATTERN,
  FORWARD_MAX_TARGETS,
  FieldErrorCode,
  GATEWAY_CLIENT_RATE_LIMIT,
  GATEWAY_PATH,
  GATEWAY_VERSION,
  GROUP_DM_MAX_RECIPIENTS,
  GUILD_CREATED_JOIN_SOURCE,
  GUILD_CREATE_RATE,
  GUILD_NAME_MAX,
  GUILD_NAME_MIN,
  GUILD_PURGE_AFTER_MS,
  GatewayCloseCodes,
  GatewayOpcodes,
  GuildScheduledEventEntityType,
  GuildScheduledEventPrivacyLevel,
  GuildScheduledEventStatus,
  IMAGE_LIMITS,
  INTERNAL_ALPHA_CODES_PATH,
  INTERNAL_SECRET_HEADER,
  INTERNAL_VOICE_STATE_PATH,
  INTERNAL_VOICE_STATE_SYNC_PATH,
  INTERNAL_VOICE_TREE_CHANGED_PATH,
  INTERNAL_VOICE_TREE_PATH,
  INVITE_BATCH_MAX,
  JUMBO_EMOJI_MAX,
  MAX_ACTIVE_THREADS,
  MAX_ANIMATED_EMOJIS,
  MAX_ANIMATION_FRAMES,
  MAX_ANIMATION_PIXELS,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_STATIC_EMOJIS,
  MAX_THREAD_MEMBERS,
  MAX_UPLOAD_IMAGE_BYTES,
  MINIMUM_AGE,
  MIN_UPLOAD_IMAGE_BYTES,
  MOVED_IN_VOICE_PERMISSIONS,
  MUTE_DURATIONS,
  MessageFlags,
  MessageReferenceType,
  MessageType,
  NOISE_SUPPRESSION_MODES,
  NON_RESUMABLE_CLOSE_CODES,
  NOTIFY_OVERRIDES_MAX,
  NotificationLevel,
  NotifyRoutes,
  NxDataFix,
  NxPermissions,
  OverwriteType,
  P1Routes,
  PASSWORD_MAX,
  PASSWORD_MIN,
  PASSWORD_RESET_TTL_MS,
  PHASE1_DISPATCH_EVENTS,
  POLL_ANSWERS_MAX,
  POLL_ANSWERS_MIN,
  POLL_ANSWER_MAX,
  POLL_DURATIONS_HOURS,
  POLL_DURATION_DEFAULT_HOURS,
  POLL_DURATION_MAX_HOURS,
  POLL_QUESTION_MAX,
  PRIORITY_SPEAKER_DUCK,
  PRIVATE_CHANNEL_PERMISSIONS,
  PUBLIC_APP_SETTING_KEYS,
  PURGE_CHUNK,
  PURGE_MAX,
  Permissions,
  PollLayoutType,
  RAID_MINUTES_MAX,
  RAID_MINUTES_MIN,
  RAID_NEW_ACCOUNT_MESSAGE_S,
  RAID_STATE_KEY,
  RELEASE_CHANNELS,
  RELEASE_UPLOAD_MAX_BYTES,
  RELEASE_VERSION_PATTERN,
  REPORT_CATEGORIES,
  REPORT_DEDUPE_MS,
  REPORT_NOTE_MAX,
  REPORT_RATE,
  RESERVED_NAME_CATEGORIES,
  RESERVED_NX_PERMISSIONS,
  RESERVED_USERNAMES,
  RESTARTABLE_SERVICES,
  RESTORE_WINDOW_MS,
  RETIRED_FEATURE_FLAGS,
  REVIEW_IDLE_MS,
  ROLE_ICONS_SHOWN_MAX,
  ROLE_ICON_MAX_BYTES,
  ROLE_ICON_MAX_SIDE,
  ROLE_ICON_MODES,
  ROLE_ICON_SIZE,
  ROLE_POKE_RULES,
  RelationshipType,
  Routes,
  SCHEDULED_EVENT_LIMITS,
  SEND_MESSAGES_DEPENDENT,
  SERVER_CREATION_MODES,
  SERVER_ONLY_PERMISSIONS,
  SLOWMODE_CHOICES,
  SNOWFLAKE_EPOCH_MS,
  SNOWFLAKE_MAX_PROCESS,
  SNOWFLAKE_MAX_SEQUENCE,
  SNOWFLAKE_MAX_WORKER,
  STAFF_GRANTABLE,
  STAFF_PERMISSIONS,
  STAFF_REVIEW_CLAUSE,
  STAFF_REVIEW_MARKER,
  STAFF_REVIEW_TEXT,
  SUSPEND_MAX_DAYS,
  SUSPEND_PRESET_DAYS,
  ScheduledEventErrorCode,
  ScheduledEventRoutes,
  SnowflakeGenerator,
  SocialRoutes,
  THREAD_AUTO_ARCHIVE_DURATIONS,
  THREAD_CHANNEL_TYPES,
  THREAD_NAME_MAX,
  THREAD_RATE_LIMIT_MAX,
  THUMB_LINK_TTL_MS,
  THUMB_MAX_SIDE,
  TIMED_OUT_ALLOWED,
  TLS_ALERT_DAYS,
  TOKEN_ISSUER,
  ThreadRoutes,
  USERNAME_MAX,
  USERNAME_MIN,
  USER_NOTES_MAX,
  USER_NOTE_MAX_LENGTH,
  USER_WARNINGS_PATH,
  UserFlags,
  VIOLATION_WEIGHTS,
  VOICE_ACTIVITY_PING_MS,
  VOICE_NX,
  VOICE_PUBLISH_NEGOTIATE_MS,
  VOICE_RECEIVE_SLOTS,
  VOICE_SDP_PER_10S,
  VOICE_SEND_PAUSE_MS,
  VOICE_SLOT_LINGER_MS,
  VOICE_STATUS_MAX,
  VOICE_STREAM_AUDIO_SLOTS,
  VOICE_TOKEN_AUDIENCE,
  VOICE_TOKEN_TTL_S,
  VOICE_VIDEO_SLOTS,
  VOICE_WS_PATH,
  VoiceCloseCodes,
  VoiceCloseCodesExtra,
  WARNING_REPORT_CATEGORY,
  WORD_FILTERS_MAX,
  WORD_FILTER_TERM_MAX,
  activityStart,
  ageOn,
  alertField,
  applyGuildSettingsPatch,
  attachmentsPerMessage,
  auditActionMatches,
  auditHashInput,
  automodActionAllowed,
  canDoUserAction,
  canHaveThreads,
  canonicalJson,
  channelMuted,
  chatImageBytes,
  compareAppVersions,
  compareRolesTopFirst,
  compareScheduledEvents,
  compareSnowflakes,
  compileKeywordMatcher,
  computeAuditHash,
  computeBasePermissions,
  computeChannelPermissions,
  computeMovedInVoicePermissions,
  computeThreadPermissions,
  confusableSkeleton,
  customEmojiPath,
  deconstructSnowflake,
  defaultAppSettings,
  defaultFeatureFlag,
  defaultGuildSettings,
  dispatch,
  displayNameLength,
  effectiveLevel,
  effectiveSignupMode,
  emojiNameFrom,
  emojiOfToken,
  escapeLikePattern,
  fitsMediaBox,
  formatCustomEmoji,
  formatElapsed,
  formatIsoDate,
  getDesktopBridge,
  guildLevel,
  guildMuted,
  guildSettingsAreDefault,
  hasAnyPermission,
  hasAnyStaffPermission,
  hasPermission,
  hasStaffPermission,
  iconRoleOf,
  iconRolesForMode,
  iconRolesOf,
  inheritedLevel,
  inviteBatchState,
  isAdminApiError,
  isAfkTimeout,
  isAnnouncementActive,
  isApiError,
  isAutoArchiveDuration,
  isAutoModAlertEmbed,
  isBuiltinStaffRoleId,
  isChannelEvent,
  isEmojiName,
  isFeatureFlagKey,
  isMfaRequired,
  isMuteDuration,
  isNewerConfig,
  isReservedUsername,
  isRetiredFlagKey,
  isSingleEmoji,
  isSnowflake,
  isStaffPermissionKey,
  isThreadChannelType,
  isTimedOut,
  isUnfinishedEvent,
  isVoiceChannelType,
  keywordProblem,
  looksLikeEmail,
  maintenancePhase,
  makeSnowflake,
  maskEmail,
  matchKeywords,
  matchedAsWritten,
  matchesReservedName,
  mediaBoxWidth,
  mediaScaleDown,
  memberTopRank,
  mentionCounts,
  messageNotifies,
  mouseButtonToPttCode,
  muteActive,
  muteConfigAt,
  nextMuteEnd,
  normalizeEmail,
  normalizeForSearch,
  normalizeNote,
  normalizeUsername,
  normalizeVoiceStatus,
  nxPermissionNames,
  nxPermissionsFrom,
  overrideIsDefault,
  overrideOf,
  overwritesInSync,
  parseAuditActionFilter,
  parseCustomEmoji,
  parseIsoDate,
  parsePermissions,
  parseReactionEmoji,
  parseTwoStepCode,
  permissionChannelFromWire,
  permissionContextFromWire,
  permissionNames,
  permissionThreadFromWire,
  permissionsFrom,
  playingActivity,
  playingOf,
  pokeAllowedByRoles,
  publicAppSettings,
  raidActive,
  reactionRouteEmoji,
  reactionRouteOfToken,
  readGameActivity,
  releaseFileKind,
  reportCategory,
  reportTier,
  resolveFlag,
  resolveFlags,
  roleRank,
  rolloutBucket,
  sameReactionEmoji,
  sanitizeDisplayName,
  scheduledEventTransitionAllowed,
  searchPattern,
  serializePermissions,
  snowflakeDate,
  snowflakeFromTimestamp,
  snowflakeTimestamp,
  standingFor,
  threadArchivesAt,
  threadNameFrom,
  toAsciiDigits,
  tokenOfEmoji,
  uniqueMentionCount,
  validateAppSetting,
  validateBirthDate,
  validateDisplayName,
  validateEmail,
  validatePassword,
  validateUsername,
  verifyAuditChain,
  visibleConsoleText
};
//# sourceMappingURL=index.js.map